import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Row, Col, Dropdown } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Navbar, CustomButton, LoadingSpinner, CustomModal } from '../components';
import { avatarsAPI, authAPI, plansAPI, projectsAPI, paymentsAPI } from '../services/api';
import './CreateAvatarPage.css';

// Normalize avatar type name for matching (API: "For Loved Once"/"For Me", UI: "for loved once"/"for a loved one"/"for myself")
const normalizeAvatarTypeName = (name) => {
  if (!name || typeof name !== 'string') return '';
  const n = name.toLowerCase().trim();
  if (n.includes('loved') && (n.includes('one') || n.includes('once'))) return 'for loved once';
  if (n.includes('myself') || n === 'for me') return 'for myself';
  return n;
};

// Extensions and MIME prefixes for video/image/audio — these are ignored during upload (files + folders)
const IGNORED_MEDIA_EXTENSIONS = [
  '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.ico', '.tiff', '.tif', '.heic', '.heif', '.raw', '.cr2', '.nef', '.orf', '.sr2', '.psd', '.ai', '.eps',
  '.mp4', '.webm', '.mov', '.avi', '.mkv', '.m4v', '.wmv', '.flv', '.mpeg', '.mpg', '.3gp', '.ogv', '.ts', '.mts', '.m2ts',
  '.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.wma', '.aiff', '.aif', '.opus', '.weba', '.amr', '.au', '.ra', '.rm', '.wv', '.ape', '.dsd',
  '.sql'
];
const IGNORED_MEDIA_MIME_PREFIXES = ['image/', 'video/', 'audio/'];
const KEEP_ARCHIVE_EXTENSIONS = ['.zip', '.rar', '.7z', '.tar', '.gz', '.bz2', '.xz'];

const isVideoImageOrAudioFile = (file) => {
  if (!file || !file.name) return false;
  const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
  if (KEEP_ARCHIVE_EXTENSIONS.includes(ext)) return false; // never filter archives
  if (IGNORED_MEDIA_EXTENSIONS.includes(ext)) return true;
  const mime = (file.type || '').toLowerCase();
  return IGNORED_MEDIA_MIME_PREFIXES.some((p) => mime.startsWith(p));
};

// Block .xls, .xlsx, .exe from upload list (folder or drag-and-drop)
const BLOCKED_EXTENSIONS = ['.xls', '.xlsx', '.xlsm', '.exe'];
const isBlockedFile = (file) => {
  if (!file || !file.name) return false;
  const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
  return BLOCKED_EXTENSIONS.includes(ext);
};

// Treat API placeholder entries as non-invitees so we show empty state instead of "You" / you@example.com.
const isPlaceholderInviteEntry = (email, name) => {
  const e = (email || '').trim().toLowerCase();
  const n = (name || '').trim().toLowerCase();
  if (e === 'you@example.com' || e === 'example@example.com') return true;
  if (n === 'you' && (!e || e.includes('example'))) return true;
  return false;
};

// Deduplicate emails (case-insensitive) while keeping stable order.
// If duplicates exist, prefer any truthy `selected` and fill missing `name`.
const dedupeEmailObjects = (emails) => {
  if (!Array.isArray(emails)) return [];
  const seen = new Map(); // key=emailLower -> index in out
  const out = [];
  for (const item of emails) {
    if (!item) continue;
    const raw = (item.email || '').trim();
    if (!raw) continue;
    const key = raw.toLowerCase();
    if (!seen.has(key)) {
      const normalized = { ...item, email: raw };
      seen.set(key, out.length);
      out.push(normalized);
      continue;
    }
    const idx = seen.get(key);
    const existing = out[idx];
    out[idx] = {
      ...existing,
      selected: Boolean(existing.selected) || Boolean(item.selected),
      name: (existing.name && String(existing.name).trim()) ? existing.name : item.name,
    };
  }
  return out;
};

const CREATE_AVATAR_PAYMENT_DRAFT_KEY = 'create_avatar_payment_draft';
const CREATE_AVATAR_PAYMENT_DRAFT_KEY_PREFIX = `${CREATE_AVATAR_PAYMENT_DRAFT_KEY}_`;
const CREATE_AVATAR_AUTH_FINGERPRINT_KEY = 'create_avatar_auth_fingerprint';
const ACTIVATED_PLAN_KEY_PREFIX = 'activated_plan_for_user_';

const CreateAvatarPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [avatarType, setAvatarType] = useState('for myself');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [actualFiles, setActualFiles] = useState([]); // Store actual File objects
  const [showAllUploadedFiles, setShowAllUploadedFiles] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('yearly');
  const [autoRenew, setAutoRenew] = useState(true);
  const [userPlanType, setUserPlanType] = useState(null);
  const [extractedEmails, setExtractedEmails] = useState([]); // Emails extracted from documents
  const [selectedEmails, setSelectedEmails] = useState([]); // Selected emails for sending
  const [extractingEmails, setExtractingEmails] = useState(false); // Loading state for email extraction
  const [avatarPlans, setAvatarPlans] = useState([]); // Avatar types with plans from API
  const [loadingPlans, setLoadingPlans] = useState(false); // Loading state for avatar plans
  const [selectedPlanId, setSelectedPlanId] = useState(null); // Selected plan ID for loved one
  const [selectedPlan, setSelectedPlan] = useState(null); // Normalized selected plan for checkout
  const [suggestionFlag, setSuggestionFlag] = useState(false); // Flag to indicate if plan should be auto-selected
  const [paymentActivatedPlan, setPaymentActivatedPlan] = useState(null); // { avatar_plan_id, plan_interval, subscription_id, invoice_id }
  const [userId, setUserId] = useState(null); // Cached user ID to avoid multiple API calls
  const [pendingAvatarData, setPendingAvatarData] = useState(null); // Store avatar data while processing payment

  const uploadedFilesRef = useRef(null);
  const UPLOADED_FILES_COLLAPSED_COUNT = 6;
  const [showInviteModal, setShowInviteModal] = useState(false); // Show invite email modal after avatar creation
  const [invitePromptEmails, setInvitePromptEmails] = useState([]); // Emails from invite_prompt in API response
  const [inviteRows, setInviteRows] = useState([]); // Rows shown in invite modal (selected + action)
  const [createdAvatarId, setCreatedAvatarId] = useState(null); // Store created avatar ID for invite API call
  const [sendingInvites, setSendingInvites] = useState(false); // Loading state for sending invites
  const [avatarPlanDetails, setAvatarPlanDetails] = useState(null); // Store avatar plan pricing options
  const [loadingAvatarPlan, setLoadingAvatarPlan] = useState(false); // Loading state for avatar plan
  const [showAllInviteRows, setShowAllInviteRows] = useState(false); // Control whether to show all invite rows or just summary
  const [openDropdownId, setOpenDropdownId] = useState(null); // Track which dropdown is open
  const [dropdownOpensUpward, setDropdownOpensUpward] = useState({}); // Track which dropdowns should open upward
  const [dropdownPosition, setDropdownPosition] = useState({}); // { [rowId]: { left, top, width } } for fixed-position dropdown
  const [relations, setRelations] = useState([]); // Relations from API
  const [loadingRelations, setLoadingRelations] = useState(false); // Loading state for relations
  const [selectedRelation, setSelectedRelation] = useState(null); // Selected relation ID
  const authCheckedRef = useRef(false);
  const userFetchedRef = useRef(false);
  const hasRestoredPaymentDraftRef = useRef(false);
  const restoredPlanIdRef = useRef(null);
  const [userData, setUserData] = useState(null); // Cache full user data

  const getCurrentSessionUserId = () => {
    const fromState = Number(userId);
    if (Number.isFinite(fromState) && fromState > 0) return fromState;
    const fromSession = Number.parseInt(sessionStorage.getItem('user_id') || '', 10);
    return Number.isFinite(fromSession) && fromSession > 0 ? fromSession : null;
  };

  const getPaymentDraftKey = (uid) => `${CREATE_AVATAR_PAYMENT_DRAFT_KEY_PREFIX}${uid}`;
  const getActivatedPlanKey = (uid) => `${ACTIVATED_PLAN_KEY_PREFIX}${uid}`;

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors }
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: ''
    }
  });

  // Check authentication on mount and fetch user ID once
  useEffect(() => {
    if (authCheckedRef.current) return;

    const token = localStorage.getItem('access_token');
    const fingerprint = token ? token.slice(-24) : '';
    const cachedFingerprint = sessionStorage.getItem(CREATE_AVATAR_AUTH_FINGERPRINT_KEY) || '';
    if (cachedFingerprint && fingerprint && cachedFingerprint !== fingerprint) {
      sessionStorage.removeItem('user_fetched');
      sessionStorage.removeItem('user_id');
      sessionStorage.removeItem('paypal_capture_result');
      const keysToDelete = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (!key) continue;
        if (
          key.startsWith(CREATE_AVATAR_PAYMENT_DRAFT_KEY_PREFIX) ||
          key.startsWith(ACTIVATED_PLAN_KEY_PREFIX) ||
          key.startsWith('payment_processed_') ||
          key.startsWith('payment_processing_')
        ) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach((key) => sessionStorage.removeItem(key));
    }
    if (fingerprint) {
      sessionStorage.setItem(CREATE_AVATAR_AUTH_FINGERPRINT_KEY, fingerprint);
    }

    if (!token) {
      authCheckedRef.current = true;
      toast.info('Please sign in to create an avatar');
      navigate('/signin');
    } else {
      authCheckedRef.current = true;
      setIsAuthenticated(true);
      fetchUserOnce(); // Fetch user ID only once
      fetchAvatarPlans();
      fetchRelations(); // Fetch relations for dropdown
    }
  }, [navigate]);

  useEffect(() => {
    const current = getCurrentSelectedPlan();
    setSelectedPlan(current);
  }, [avatarPlans, avatarType, selectedPlanId, billingPeriod]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('paypal_capture_result');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed?.avatar_plan_id || !parsed?.plan_interval) return;
      setPaymentActivatedPlan(parsed);
      const currentUid = getCurrentSessionUserId();
      if (currentUid) {
        sessionStorage.setItem(
          getActivatedPlanKey(currentUid),
          JSON.stringify({
            avatar_plan_id: parsed.avatar_plan_id,
            plan_interval: parsed.plan_interval,
            auto_renew: Boolean(parsed.auto_renew),
            subscription_id: parsed.subscription_id || null,
            invoice_id: parsed.invoice_id || null,
            invoice_download_url: parsed.invoice_download_url || null,
            order_id: parsed.order_id || null,
          })
        );
      }
      toast.success('Subscription activated successfully.', { toastId: 'subscription-activated' });
    } catch (err) {
      console.warn('Failed to read captured payment context:', err);
    }
  }, []);

  useEffect(() => {
    // Restore paid plan activation for this user (prevents re-payment prompt after file reupload/reload).
    try {
      const currentUid = getCurrentSessionUserId();
      if (!currentUid) return;
      const raw = sessionStorage.getItem(getActivatedPlanKey(currentUid));
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed?.avatar_plan_id || !parsed?.plan_interval) return;
      setPaymentActivatedPlan(parsed);
    } catch (err) {
      console.warn('Failed to restore activated plan from session:', err);
    }
  }, [userId]);

  useEffect(() => {
    // Restore per-user form draft after returning from payment flow.
    try {
      const currentUid = getCurrentSessionUserId();
      if (!currentUid) return;
      const rawDraft = sessionStorage.getItem(getPaymentDraftKey(currentUid));
      if (!rawDraft) return;
      const draft = JSON.parse(rawDraft);
      if (!draft || Number(draft.user_id) !== Number(currentUid)) return;

      if (typeof draft.firstName === 'string') setValue('firstName', draft.firstName);
      if (typeof draft.lastName === 'string') setValue('lastName', draft.lastName);
      if (typeof draft.avatarType === 'string' && draft.avatarType) setAvatarType(draft.avatarType);
      if (typeof draft.selectedRelation === 'string' && draft.selectedRelation) setSelectedRelation(draft.selectedRelation);
      if (draft.selectedPlanId !== undefined && draft.selectedPlanId !== null) setSelectedPlanId(draft.selectedPlanId);
      if (typeof draft.billingPeriod === 'string' && draft.billingPeriod) setBillingPeriod(draft.billingPeriod);
      if (typeof draft.autoRenew === 'boolean') setAutoRenew(draft.autoRenew);

      // Restore file list display only (browser cannot restore true File objects after redirect).
      if (Array.isArray(draft.uploadedFiles) && draft.uploadedFiles.length > 0) {
        const restoredDisplayFiles = draft.uploadedFiles.map((file, idx) => ({
          id: file?.id ?? `restored-${Date.now()}-${idx}`,
          name: file?.name || `Uploaded file ${idx + 1}`,
          size: Number(file?.size || 0),
          type: file?.type || '',
          isRestored: true,
        }));
        setUploadedFiles(restoredDisplayFiles);
      }
      hasRestoredPaymentDraftRef.current = true;
    } catch (err) {
      console.warn('Failed to restore create-avatar payment draft:', err);
    }
  }, [setValue, userId]);

  useEffect(() => {
    // Keep paid plan auto-selected for this logged-in user session.
    if (!paymentActivatedPlan?.avatar_plan_id || !Array.isArray(avatarPlans) || avatarPlans.length === 0) return;

    // Prevent overwriting user selection if we've already restored this plan
    if (restoredPlanIdRef.current === paymentActivatedPlan.avatar_plan_id) return;

    const paidPlanId = Number(paymentActivatedPlan.avatar_plan_id);
    if (!Number.isFinite(paidPlanId)) return;

    let matchedAvatarType = null;
    let matchedPlan = null;
    for (const avatarTypeItem of avatarPlans) {
      const plans = Array.isArray(avatarTypeItem?.plans) ? avatarTypeItem.plans : [];
      const found = plans.find((p) => Number(p?.id) === paidPlanId);
      if (found) {
        matchedAvatarType = avatarTypeItem;
        matchedPlan = found;
        break;
      }
    }
    if (!matchedAvatarType || !matchedPlan) return;

    if (matchedAvatarType?.name) setAvatarType(String(matchedAvatarType.name).toLowerCase());
    setSelectedPlanId(Number(matchedPlan.id));
    const paidInterval = String(paymentActivatedPlan.plan_interval || '').toLowerCase();
    if (paidInterval === 'monthly' || paidInterval === 'yearly') {
      setBillingPeriod(paidInterval);
    }

    // Mark this plan as restored so we don't overwrite manual changes later
    restoredPlanIdRef.current = paymentActivatedPlan.avatar_plan_id;
  }, [paymentActivatedPlan, avatarPlans]);

  // Scroll to top when email extraction starts (backup scroll)
  useEffect(() => {
    if (extractingEmails) {
      // Ensure we're at the absolute top
      window.scrollTo({ top: 0, behavior: 'auto' });
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
      }
      if (document.body) {
        document.body.scrollTop = 0;
      }
    }
  }, [extractingEmails]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside any dropdown
      const clickedElement = event.target;
      const isInsideDropdown = clickedElement.closest('.invite-action-select-wrapper');
      if (!isInsideDropdown && openDropdownId !== null) {
        setOpenDropdownId(null);
      }
    };

    if (openDropdownId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId]);

  // Auto-select default plan when plan modal opens
  useEffect(() => {
    if (!showPlanModal || !avatarPlans.length) return;

    // Find the selected avatar type
    const selectedAvatarType = avatarPlans.find(avatarTypeItem =>
      normalizeAvatarTypeName(avatarTypeItem.name) === normalizeAvatarTypeName(avatarType)
    );
    if (!selectedAvatarType?.plans?.length) return;

    const availablePlans = selectedAvatarType.plans;

    // If a plan is already selected, sync billing period with it
    if (selectedPlanId) {
      const selectedPlan = availablePlans.find(p => Number(p.id) === Number(selectedPlanId));
      if (selectedPlan) {
        // Sync billing period to match the selected plan (only if it doesn't match)
        const isFree = !selectedPlan.monthly_price_usd && !selectedPlan.yearly_price_usd && !selectedPlan.day_price_usd;
        const isMonthly = selectedPlan.monthly_price_usd && !selectedPlan.yearly_price_usd;
        const isYearly = selectedPlan.yearly_price_usd && !selectedPlan.monthly_price_usd;

        if (isFree && billingPeriod !== 'free') {
          setBillingPeriod('free');
        } else if (isMonthly && billingPeriod !== 'monthly') {
          setBillingPeriod('monthly');
        } else if (isYearly && billingPeriod !== 'yearly') {
          setBillingPeriod('yearly');
        }
        return;
      }
    }

    // No plan selected - select default based on priority: Free > Monthly > Yearly
    const freePlan = availablePlans.find(p => !p.monthly_price_usd && !p.yearly_price_usd && !p.day_price_usd);
    const monthlyPlan = availablePlans.find(p => p.monthly_price_usd);
    const yearlyPlan = availablePlans.find(p => p.yearly_price_usd);

    // Select the first available plan in priority order
    if (freePlan) {
      setSelectedPlanId(freePlan.id);
      setBillingPeriod('free');
    } else if (monthlyPlan) {
      setSelectedPlanId(monthlyPlan.id);
      setBillingPeriod('monthly');
    } else if (yearlyPlan) {
      setSelectedPlanId(yearlyPlan.id);
      setBillingPeriod('yearly');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPlanModal, avatarPlans, avatarType, selectedPlanId]);

  // Decide if dropdown should open upward so it stays inside the modal and doesn't overlap content below (e.g. "Drag & Drop").
  // Use the invite list (or modal) as the boundary, and open upward when there's more space above than below.
  const EST_DROPDOWN_HEIGHT = 220;
  const getShouldOpenUpward = (buttonElement) => {
    if (!buttonElement || typeof buttonElement.getBoundingClientRect !== 'function') return false;
    const rect = buttonElement.getBoundingClientRect();
    const scrollContainer =
      buttonElement.closest('.invite-rows-full-list') ||
      buttonElement.closest('.invite-rows-preview') ||
      buttonElement.closest('.invite-emails-list') ||
      null;
    const containerRect = scrollContainer?.getBoundingClientRect?.();
    const containerBottom = containerRect?.bottom ?? window.innerHeight;
    const containerTop = containerRect?.top ?? 0;

    const spaceBelow = containerBottom - rect.bottom;
    const spaceAbove = rect.top - containerTop;
    // Open upward if there's less room below than above, or if below is too small for the menu
    return spaceBelow < Math.max(EST_DROPDOWN_HEIGHT, spaceAbove) || spaceBelow < EST_DROPDOWN_HEIGHT;
  };

  // Fallback: re-check after dropdown is rendered (e.g. if viewport was resized)
  const checkDropdownPosition = (buttonElement, rowId) => {
    if (!buttonElement) return;
    setTimeout(() => {
      const dropdownElement = document.querySelector(`[data-row-id="${rowId}"] .invite-action-dropdown-menu`);
      if (!dropdownElement) return;
      const rect = buttonElement.getBoundingClientRect();
      const dropdownHeight = dropdownElement.offsetHeight || 150;
      const scrollContainer =
        buttonElement.closest('.invite-rows-full-list') ||
        buttonElement.closest('.invite-rows-preview') ||
        buttonElement.closest('.invite-emails-list') ||
        null;
      const containerRect = scrollContainer?.getBoundingClientRect?.();
      const containerBottom = containerRect?.bottom ?? window.innerHeight;
      const containerTop = containerRect?.top ?? 0;

      const spaceBelow = containerBottom - rect.bottom;
      const spaceAbove = rect.top - containerTop;
      const minHeight = Math.max(dropdownHeight + 20, 200);
      const shouldOpenUpward =
        spaceBelow < minHeight || (spaceBelow < spaceAbove && spaceAbove > dropdownHeight);
      setDropdownOpensUpward(prev => ({ ...prev, [rowId]: shouldOpenUpward }));

      // Keep dropdown anchored to the button without changing scroll position.
      // Using fixed positioning avoids forcing the scroll container to overflow: visible (which can reset scrollTop).
      const top = shouldOpenUpward ? (rect.top - dropdownHeight - 4) : (rect.bottom + 4);
      setDropdownPosition(prev => ({
        ...prev,
        [rowId]: { left: rect.left, top: Math.max(8, top), width: rect.width }
      }));
    }, 10);
  };

  // Helper function to get icon for an option
  const getOptionIcon = (value) => {
    if (value === 'send_invite') {
      return (
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      );
    } else if (value === 'gift_month' || value === 'gift_year') {
      return (
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
        </svg>
      );
    }
    return null;
  };

  // Fetch user ID once and cache it - prevents multiple API calls
  const fetchUserOnce = async () => {
    // Check if already fetched in this session (across page navigations)
    const userFetchedInSession = sessionStorage.getItem('user_fetched');
    const storedUserId = sessionStorage.getItem('user_id');
    const storedUserData = sessionStorage.getItem('user_data');

    // If already fetched/cached, try to restore state
    if (userFetchedInSession && storedUserId) {
      console.log('CreateAvatarPage: User already fetched in this session, checking cache...');

      const cachedUserId = parseInt(storedUserId, 10);
      setUserId(cachedUserId);

      // Restore full user data if available
      if (storedUserData) {
        try {
          const parsedUserData = JSON.parse(storedUserData);
          setUserData(parsedUserData);
          console.log('CreateAvatarPage: Restored userData from sessionStorage');
          return cachedUserId;
        } catch (e) {
          console.warn('Failed to parse cached user_data, will refetch');
        }
      } else {
        // If we have userId but not full userData, we might want to fetch if we need the name
        // But to respect the 'active session' logic, we'll return userId and let a background effect fetch if needed, 
        // OR just proceed to fetch here if we really need userData.
        // For the subscription card, we need `userData`. Let's fall through to fetch if userData is missing.
        console.log('CreateAvatarPage: Missing cached userData, proceeding to fetch.');
      }
    }

    // Double check to prevent duplicate calls (React StrictMode or re-renders)
    // Only skip if we have BOTH userId and userData (since we need both now)
    if (userFetchedRef.current && userId && userData) {
      console.log('CreateAvatarPage: User data already fully loaded, skipping API call');
      return userId;
    }

    try {
      // Set flags immediately to prevent duplicate calls (synchronous)
      userFetchedRef.current = true;
      sessionStorage.setItem('user_fetched', 'true'); // Mark in session immediately
      console.log('CreateAvatarPage: Fetching user data from API...');

      const userResponse = await authAPI.getCurrentUser();
      if (userResponse.status && userResponse.data) {
        const fetchedUserData = userResponse.data;
        const fetchedUserId = fetchedUserData.id || fetchedUserData.user_id;

        // Cache the full user data and userId
        setUserData(fetchedUserData);
        setUserId(fetchedUserId);
        sessionStorage.setItem('user_id', fetchedUserId.toString());
        sessionStorage.setItem('user_data', JSON.stringify(fetchedUserData)); // Cache full data

        // Check if user signed up with "for myself" plan (same logic as fetchUserPlanType)
        const userAvatarType = fetchedUserData.avatar_type || fetchedUserData.plan_type || fetchedUserData.plan_name;
        const avatarTypeLower = userAvatarType ? userAvatarType.toLowerCase() : '';

        // Check if user has free plan or signed up with "for myself"
        if (avatarTypeLower.includes('myself') ||
          avatarTypeLower === 'for myself' ||
          avatarTypeLower.includes('free') ||
          !fetchedUserData.plan_interval) { // If no plan_interval, likely free plan
          setUserPlanType('for myself');
          setAvatarType('for myself'); // Set as default
        }

        console.log('CreateAvatarPage: User data fetched and cached. UserId:', fetchedUserId);
        return fetchedUserId;
      }
    } catch (error) {
      console.error('Error fetching user ID:', error);
      // Reset refs on error so it can retry if needed
      if (error?.status !== 401) { // Don't reset on auth errors
        userFetchedRef.current = false;
        sessionStorage.removeItem('user_fetched');
        sessionStorage.removeItem('user_id');
        sessionStorage.removeItem('user_data');
      }
      throw error;
    }
    return null;
  };

  // Removed fetchUserPlanType - logic is now integrated into fetchUserOnce to prevent duplicate API calls

  const normalizeSelectedPlan = (plan, selectedInterval = billingPeriod) => {
    if (!plan) return null;

    // Determine interval based on selectedInterval or plan properties
    let interval;
    if (selectedInterval === 'free' || (!plan.monthly_price_usd && !plan.yearly_price_usd && !plan.day_price_usd)) {
      interval = 'free_trial';
    } else if (selectedInterval === 'monthly' || plan.monthly_price_usd) {
      interval = 'monthly';
    } else if (selectedInterval === 'yearly' || plan.yearly_price_usd) {
      interval = 'yearly';
    } else {
      interval = plan.interval_name || plan.interval || plan.plan_interval || 'free_trial';
    }

    let priceUsd = 0;
    if (interval === 'monthly') {
      priceUsd = Number(plan.monthly_price_usd ?? plan.price_usd ?? 0);
    } else if (interval === 'yearly') {
      priceUsd = Number(plan.yearly_price_usd ?? plan.price_usd ?? 0);
    } else {
      // Free trial or other intervals
      priceUsd = Number(plan.price_usd ?? plan.day_price_usd ?? 0);
    }

    return {
      avatar_plan_id: Number(plan.avatar_plan_id ?? plan.plan_id ?? plan.id),
      interval,
      price_usd: Number.isFinite(priceUsd) ? priceUsd : 0,
      token_limit: Number(plan.token_limit ?? plan.tokens_limit ?? plan.max_tokens ?? 0),
      plan_days: Number(plan.plan_days ?? plan.days ?? 0),
      storage: plan.max_storage_gb ?? plan.storage ?? plan.storage_limit ?? plan.storage_limit_gb ?? null,
      family_members: Number(plan.max_family_members ?? plan.family_members ?? plan.family_member_limit ?? plan.members_limit ?? 0),
    };
  };

  const getCurrentSelectedPlan = () => {
    const selectedAvatarType = avatarPlans.find(
      (avatarTypeItem) => normalizeAvatarTypeName(avatarTypeItem.name) === normalizeAvatarTypeName(avatarType)
    );
    if (!selectedAvatarType?.plans?.length) return null;
    const chosen = selectedAvatarType.plans.find((p) => Number(p.id) === Number(selectedPlanId));
    if (!chosen) return null;
    return normalizeSelectedPlan(chosen, billingPeriod);
  };

  const isSelectedPlanActivated = () => {
    const current = getCurrentSelectedPlan();
    if (!current) return false;
    if (current.price_usd <= 0) return true;
    if (!paymentActivatedPlan) return false;
    return (
      Number(paymentActivatedPlan.avatar_plan_id) === Number(current.avatar_plan_id) &&
      String(paymentActivatedPlan.plan_interval) === String(current.interval)
    );
  };

  // Fetch avatar plans from API
  const fetchAvatarPlans = async () => {
    setLoadingPlans(true);
    try {
      let response;
      try {
        // Checkout flow source (required endpoint): GET /api/avatar-plans
        response = await plansAPI.getAvatarPlans();
      } catch (checkoutErr) {
        console.warn('GET /api/avatar-plans failed, falling back to legacy plans endpoint:', checkoutErr);
        response = await plansAPI.getAvatarPlans();
      }

      // Handle API response structure: { status, data: { avatar_types: [...] } }
      let avatarTypes = [];

      if (response && response.data && response.data.avatar_types && Array.isArray(response.data.avatar_types)) {
        avatarTypes = response.data.avatar_types;
      } else if (response && response.data && Array.isArray(response.data)) {
        avatarTypes = response.data;
      } else if (Array.isArray(response)) {
        avatarTypes = response;
      }

      // Fallback: if "For Loved Once" (id 2) has empty plans from API, use default plans so the Plan modal works
      const lovedOne = avatarTypes.find(a => (a.id === 2) || (a.name && String(a.name).toLowerCase().includes('loved')));
      if (lovedOne && (!lovedOne.plans || lovedOne.plans.length === 0)) {
        console.warn('Avatar plans API returned empty plans for "For Loved Once". Using fallback plans so the Plan modal can continue.');
        lovedOne.plans = [
          { id: 2, avatar_type_id: 2, monthly_price_usd: 19.99, yearly_price_usd: null, plan_days: 30 },
          { id: 3, avatar_type_id: 2, monthly_price_usd: null, yearly_price_usd: 150, plan_days: 365 }
        ];
      }

      if (avatarTypes.length > 0) {
        setAvatarPlans(avatarTypes);

        // If we already restored a payment draft, do not override it with defaults/suggestions.
        if (hasRestoredPaymentDraftRef.current) {
          setSuggestionFlag(false);
          return;
        }

        // Check for suggested plan (suggestion_flag: true) across all avatar types
        let suggestedPlan = null;
        let suggestedAvatarType = null;

        for (const avatarTypeItem of avatarTypes) {
          if (avatarTypeItem.plans && Array.isArray(avatarTypeItem.plans)) {
            const planWithSuggestion = avatarTypeItem.plans.find(plan => plan.suggestion_flag === true);
            if (planWithSuggestion) {
              suggestedPlan = planWithSuggestion;
              suggestedAvatarType = avatarTypeItem;
              break;
            }
          }
        }

        if (suggestedPlan && suggestedAvatarType) {
          // Set suggestion flag to true
          setSuggestionFlag(true);

          // Set avatar type to the one containing the suggested plan
          setAvatarType(suggestedAvatarType.name.toLowerCase());

          // Set billing period based on the suggested plan
          if (!suggestedPlan.monthly_price_usd && !suggestedPlan.yearly_price_usd && !suggestedPlan.day_price_usd) {
            setBillingPeriod('free');
          } else if (suggestedPlan.monthly_price_usd && !suggestedPlan.yearly_price_usd) {
            setBillingPeriod('monthly');
          } else if (suggestedPlan.yearly_price_usd && !suggestedPlan.monthly_price_usd) {
            setBillingPeriod('yearly');
          }

          // Set the selected plan ID
          setSelectedPlanId(suggestedPlan.id);

          console.log('Applied suggested plan:', {
            planId: suggestedPlan.id,
            avatarType: suggestedAvatarType.name,
            billingPeriod: suggestedPlan.monthly_price_usd ? 'monthly' : 'yearly',
            monthlyPrice: suggestedPlan.monthly_price_usd,
            yearlyPrice: suggestedPlan.yearly_price_usd
          });
        } else {
          // No suggested plan, use default behavior
          setSuggestionFlag(false);

          // Set default avatar type to first avatar type
          const firstAvatarType = avatarTypes[0];
          if (firstAvatarType.name) {
            setAvatarType(firstAvatarType.name.toLowerCase());
            // If it has a plan, set the first plan as default
            if (firstAvatarType.plans && firstAvatarType.plans.length > 0) {
              setSelectedPlanId(firstAvatarType.plans[0].id);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching avatar plans:', error);
      // Only show error if it's not a network/timeout issue or if it's a critical error
      // Don't show error on page refresh if it's just a temporary network issue
      const isNetworkError = !error?.status && (error?.message?.includes('network') || error?.message?.includes('fetch') || error?.message?.includes('timeout'));
      const isServerError = error?.status >= 500;

      // Only show error toast for non-network issues or persistent server errors
      // Silent fail for network issues on page load to avoid annoying users
      if (!isNetworkError && !isServerError) {
        toast.error('Failed to load avatar plans. Using default options.', {
          toastId: 'avatar-plans-error',
          autoClose: 3000
        });
      } else {
        // Log silently for network/server errors - default options will be used
        console.warn('Avatar plans fetch failed (network/server issue). Using default options silently.');
      }
    } finally {
      setLoadingPlans(false);
    }
  };

  // Fetch relations from API
  const fetchRelations = async () => {
    setLoadingRelations(true);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.warn('No authentication token found for fetching relations');
        return;
      }

      const apiUrl = (process.env.REACT_APP_API_URL || 'http://192.168.1.6:8090').replace(/\/$/, '');
      const response = await fetch(`${apiUrl}/api/avatars/relations`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to load relations:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        toast.error(`Failed to load relations: ${response.status} ${response.statusText}`);
        return;
      }

      const data = await response.json();
      console.log('Relations API response:', data);

      // Extract relations from response
      // Response format: { status: true, status_code: 200, data: { relations: [{ value, label }, ...] } }
      let relationsData = [];
      if (data.data && data.data.relations && Array.isArray(data.data.relations)) {
        relationsData = data.data.relations;
      } else if (data.data && Array.isArray(data.data)) {
        relationsData = data.data;
      } else if (data.relations && Array.isArray(data.relations)) {
        relationsData = data.relations;
      } else if (Array.isArray(data)) {
        relationsData = data;
      }

      console.log('Extracted relations:', relationsData.length, 'relations');
      setRelations(relationsData);
    } catch (error) {
      console.error('Error fetching relations:', error);
      toast.error('Failed to load relations. Please try again.', {
        toastId: 'relations-load-error',
        autoClose: 3000
      });
    } finally {
      setLoadingRelations(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  // Recursively read all files from a directory entry; returns { file, relativePath } so each file shows as a separate row with path
  const readAllFilesFromDirectory = async (dirEntry, basePath = '') => {
    const result = [];
    const reader = dirEntry.createReader();
    const readBatch = () =>
      new Promise((resolve, reject) => {
        reader.readEntries(resolve, reject);
      });
    let entries = [];
    do {
      entries = await readBatch();
      for (const entry of entries) {
        const fullPath = basePath ? `${basePath}/${entry.name}` : entry.name;
        if (entry.isFile) {
          const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
          if (file && file.name && (file.size === undefined || file.size >= 0)) {
            result.push({ file, relativePath: fullPath });
          }
        } else if (entry.isDirectory) {
          const subResults = await readAllFilesFromDirectory(entry, fullPath);
          result.push(...subResults);
        }
      }
    } while (entries.length > 0);
    return result;
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    const items = e.dataTransfer?.items;
    const collected = []; // array of File or { file, displayPath }

    if (items && typeof items[0]?.webkitGetAsEntry === 'function') {
      for (const item of Array.from(items)) {
        if (item.kind !== 'file') continue;
        const entry = item.webkitGetAsEntry();
        if (!entry) {
          const file = item.getAsFile();
          if (file && file.name && file.size > 0) collected.push(file);
          continue;
        }
        if (entry.isDirectory) {
          try {
            const dirResults = await readAllFilesFromDirectory(entry, entry.name);
            collected.push(...dirResults);
          } catch (err) {
            console.warn('Could not read dropped folder:', entry.name, err);
            toast.warning(`Could not read folder "${entry.name}". Use "Select Folder" instead.`, {
              toastId: 'folder-read-error',
              autoClose: 5000
            });
          }
        } else {
          const file = item.getAsFile();
          if (file && file.name && file.size > 0) collected.push(file);
        }
      }
    }

    const fallbackFiles = Array.from(e.dataTransfer.files || []).filter(
      (file) => file && file.name && file.size > 0
    );
    if (collected.length === 0) {
      if (fallbackFiles.length === 0) {
        toast.warning('No files detected. Drag files or use "Select Folder" to add one or more folders.', {
          toastId: 'no-files-dropped',
          autoClose: 4000
        });
        return;
      }
      addFiles(fallbackFiles);
      return;
    }
    const allPlainFiles = collected.length > 0 && collected.every((c) => c instanceof File);
    if (allPlainFiles && fallbackFiles.length > collected.length) {
      addFiles(fallbackFiles);
      return;
    }
    addFiles(collected);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) {
      console.warn('No files selected in file input');
      return;
    }

    const validFiles = files.filter((file) => file && file.name);
    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }

    // When user selects a folder, pass each file with its path so they show as separate rows (not under one folder)
    const isFolderSelection = validFiles[0]?.webkitRelativePath && validFiles[0].webkitRelativePath.includes('/');
    const toAdd = isFolderSelection
      ? validFiles.map((file) => ({ file, displayPath: file.webkitRelativePath || file.name }))
      : validFiles;

    addFiles(toAdd);
    e.target.value = '';
  };



  // Normalize input: accept File[] or { file, displayPath }[] (displayPath = path for folder files so each shows as separate row)
  const normalizeFileInput = (files) => {
    return files.map((item) => {
      if (item && typeof item === 'object' && item.file instanceof File) {
        return { file: item.file, displayName: item.displayPath || item.relativePath || item.file.name };
      }
      if (item instanceof File) {
        return { file: item, displayName: item.name };
      }
      return null;
    }).filter(Boolean);
  };

  const addFiles = (files) => {
    if (!files || files.length === 0) {
      console.warn('No files provided to addFiles');
      return;
    }

    const normalized = normalizeFileInput(files);

    // Skip video/image/audio and XLS/EXE — single common message for all unsupported files
    const skipped = normalized.filter((n) => isVideoImageOrAudioFile(n.file) || isBlockedFile(n.file));
    const toProcess = normalized.filter((n) => !isVideoImageOrAudioFile(n.file) && !isBlockedFile(n.file));
    if (skipped.length > 0) {
      toast.info(
        skipped.length === 1
          ? `"${skipped[0].file.name}" was not added. Video, image, audio, XLS and EXE files are not supported.`
          : `${skipped.length} file(s) were not added. Video, image, audio, XLS and EXE files are not supported.`,
        { toastId: 'unsupported-files-skipped', autoClose: 5000 }
      );
    }

    if (toProcess.length === 0) {
      return;
    }

    console.log('addFiles called with', toProcess.length, 'file(s)');
    console.log('Current actualFiles count:', actualFiles.length);
    console.log('Current uploadedFiles count:', uploadedFiles.length);

    // Maximum allowed total file size: 5 GB
    const MAX_TOTAL_SIZE = 5 * 1024 * 1024 * 1024; // 5 GB in bytes

    // Dedupe by displayName + size so folder files (with path) are separate from each other and from same-name files elsewhere
    const duplicateFiles = [];
    const uniqueItems = [];

    toProcess.forEach(({ file, displayName }) => {
      if (!file || !file.name) {
        console.warn('Invalid file object:', file);
        return;
      }
      const isDuplicate = uploadedFiles.some(
        (_, i) => uploadedFiles[i]?.name === displayName && actualFiles[i]?.size === file.size
      );
      if (isDuplicate) {
        duplicateFiles.push({ file, displayName });
      } else {
        uniqueItems.push({ file, displayName });
      }
    });

    if (duplicateFiles.length > 0) {
      const names = duplicateFiles.map((d) => d.displayName).join(', ');
      toast.warning(
        duplicateFiles.length === 1
          ? `"${duplicateFiles[0].displayName}" is already in the list.`
          : `${duplicateFiles.length} file(s) already in list: ${names.substring(0, 80)}${names.length > 80 ? '...' : ''}.`,
        { toastId: 'duplicate-files', autoClose: 5000 }
      );
    }

    if (uniqueItems.length === 0) {
      return;
    }

    const currentTotalSize = actualFiles.reduce((total, file) => total + file.size, 0);
    const newFilesTotalSize = uniqueItems.reduce((total, { file }) => total + file.size, 0);
    const totalSizeAfterAdd = currentTotalSize + newFilesTotalSize;

    if (totalSizeAfterAdd > MAX_TOTAL_SIZE) {
      const currentTotalGB = (currentTotalSize / (1024 * 1024 * 1024)).toFixed(2);
      const newFilesGB = (newFilesTotalSize / (1024 * 1024 * 1024)).toFixed(2);
      const totalGB = (totalSizeAfterAdd / (1024 * 1024 * 1024)).toFixed(2);
      toast.error(
        `Total file size exceeds 5 GB limit!\nCurrent: ${currentTotalGB} GB\nNew files: ${newFilesGB} GB\nTotal: ${totalGB} GB\nPlease remove some files or select smaller files.`,
        { toastId: 'file-size-limit', autoClose: 8000 }
      );
      return;
    }

    const baseId = Date.now();
    const newFiles = uniqueItems.map(({ file, displayName }, index) => ({
      id: `file-${baseId}-${index}-${Math.random().toString(36).slice(2, 11)}`,
      name: displayName,
      size: formatFileSize(file.size),
      type: getFileType(file.name),
      file
    }));

    const uniqueFiles = uniqueItems.map((i) => i.file);
    console.log('Adding unique files to state:', uniqueItems.map((i) => i.displayName));

    // Update states and extract emails from ONLY the new unique files (merge with existing)
    // Store actual file objects for upload (these will be sent to API with field name 'files')
    // Each file is appended with the same field name 'files' to match cURL: -F "files=@file1" -F "files=@file2"
    try {
      setActualFiles(prev => {
        const updated = [...prev, ...uniqueFiles];
        console.log('Updated actualFiles count:', updated.length);
        return updated;
      });

      setUploadedFiles(prev => {
        const updated = [...prev, ...newFiles];
        console.log('Updated uploadedFiles count:', updated.length);
        return updated;
      });

      // Extract-emails runs only when user clicks Create Avatar (not when adding files; avatar-plan flow stays separate)

      // Show success message for unique files
      if (uniqueFiles.length > 0) {
        toast.success(
          uniqueFiles.length === 1
            ? '1 file added successfully'
            : `${uniqueFiles.length} file(s) added successfully`,
          { toastId: 'files-added' }
        );
      }
    } catch (error) {
      console.error('Error adding files:', error);
      toast.error('Failed to add files. Please try again.', {
        toastId: 'files-add-error',
        autoClose: 5000
      });
    }
  };

  // Extract emails from uploaded files using API
  // Matches cURL: -F "user_id=2" -F "files=@file1" -F "files=@file2"
  // replaceExisting: if true, replaces all emails; if false, merges with existing (removing duplicates)
  const extractEmailsFromFiles = async (files, replaceExisting = false) => {
    if (!files || files.length === 0) {
      if (replaceExisting) {
        // If called to replace and no files, clear emails
        setExtractedEmails([]);
        setSelectedEmails([]);
      }
      setExtractingEmails(false);
      return;
    }

    setExtractingEmails(true);
    // Scroll to top to show the loading message (instant scroll)
    window.scrollTo({ top: 0, behavior: 'auto' });
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }

    try {
      // Get user ID - use cached value if available
      const currentUserId = userId || await getUserId();
      if (!currentUserId) {
        console.warn('Unable to get user ID for email extraction');
        setExtractingEmails(false);
        return;
      }

      // Call API to extract emails with user_id and files
      const response = await avatarsAPI.extractEmails(files, currentUserId);

      console.log('Email extraction response:', response);
      console.log('Response type:', typeof response);
      console.log('Response keys:', Object.keys(response || {}));

      // Handle API response format: { emails: ["email1@example.com", "email2@example.com"] }
      let emailArray = [];

      // Check for response.emails first (most common format)
      if (response && response.emails && Array.isArray(response.emails)) {
        console.log('Found emails in response.emails');
        emailArray = response.emails;
      }
      // Check for response.data.emails
      else if (response && response.data && response.data.emails && Array.isArray(response.data.emails)) {
        console.log('Found emails in response.data.emails');
        emailArray = response.data.emails;
      }
      // Check for response.data as array
      else if (response && response.data && Array.isArray(response.data)) {
        console.log('Found emails in response.data (array)');
        emailArray = response.data;
      }
      // Check if response is direct array
      else if (Array.isArray(response)) {
        console.log('Response is direct array');
        emailArray = response;
      }
      // Check for nested structure
      else if (response && response.data && typeof response.data === 'object' && response.data.emails) {
        console.log('Found emails in nested response.data.emails');
        emailArray = Array.isArray(response.data.emails) ? response.data.emails : [];
      }

      console.log('Extracted emailArray:', emailArray);
      console.log('EmailArray length:', emailArray.length);

      if (emailArray.length === 0) {
        console.warn('No emails found in response. Full response:', JSON.stringify(response, null, 2));
        toast.info('No emails found in the uploaded documents', {
          toastId: 'no-emails-found',
          autoClose: 3000
        });
        setExtractingEmails(false);
        return;
      }

      // Format emails with unique IDs
      // Handle both string emails and object emails
      const formattedEmails = emailArray.map((emailItem, index) => {
        // If emailItem is a string, use it directly
        if (typeof emailItem === 'string') {
          const emailAddress = emailItem.trim();
          if (!emailAddress) return null;
          const nameFromEmail = emailAddress.split('@')[0];
          return {
            id: Date.now() + index + Math.random(), // More unique ID
            email: emailAddress,
            name: nameFromEmail.charAt(0).toUpperCase() + nameFromEmail.slice(1) // Capitalize first letter
          };
        }
        // If emailItem is an object, extract email and name
        const emailAddress = (emailItem.email || emailItem.email_address || '').trim();
        if (!emailAddress) return null;
        return {
          id: Date.now() + index + Math.random(), // More unique ID
          email: emailAddress,
          name: emailItem.name || emailItem.full_name || emailAddress.split('@')[0] || 'Unknown'
        };
      }).filter(email => email !== null); // Remove any null entries

      const uniqueFormattedEmails = dedupeEmailObjects(formattedEmails);

      console.log('Formatted emails:', formattedEmails);
      console.log('Formatted emails count:', formattedEmails.length);
      console.log('Unique formatted emails count:', uniqueFormattedEmails.length);

      if (uniqueFormattedEmails.length === 0) {
        console.warn('No valid emails after formatting');
        if (replaceExisting) {
          setExtractedEmails([]);
          setSelectedEmails([]);
        }
        toast.info('No valid emails found in the uploaded documents', {
          toastId: 'no-valid-emails',
          autoClose: 3000
        });
        setExtractingEmails(false);
        return;
      }

      if (replaceExisting) {
        // Replace all emails with new ones
        setExtractedEmails(uniqueFormattedEmails);
        // Clear selected emails that are no longer in the new list
        setSelectedEmails(prevSelected => prevSelected.filter(id => uniqueFormattedEmails.some(email => email.id === id)));
        console.log('Replaced extractedEmails state with:', uniqueFormattedEmails.length, 'emails');
        // Removed toast message for email extraction success
      } else {
        // Merge with existing emails, removing duplicates
        setExtractedEmails(prevEmails => {
          // Create a map of existing emails by email address
          const existingEmailsMap = new Map(prevEmails.map(e => [e.email.toLowerCase(), e]));

          // Add new emails, avoiding duplicates
          uniqueFormattedEmails.forEach(newEmail => {
            const emailKey = newEmail.email.toLowerCase();
            if (!existingEmailsMap.has(emailKey)) {
              existingEmailsMap.set(emailKey, newEmail);
            }
          });

          // Convert back to array
          const mergedEmails = Array.from(existingEmailsMap.values());

          // Filter selected emails to only include those still in the merged list
          setSelectedEmails(prevSelected => prevSelected.filter(id => mergedEmails.some(email => email.id === id)));

          // Removed toast messages for email extraction

          console.log('Merged extractedEmails state. Previous:', prevEmails.length, 'New:', uniqueFormattedEmails.length, 'Merged:', mergedEmails.length);
          return mergedEmails;
        });
      }
    } catch (error) {
      console.error('Error extracting emails:', error);
      toast.error('Failed to extract emails from documents. Please try again.', {
        toastId: 'email-extraction-error',
        autoClose: 5000
      });
    } finally {
      setExtractingEmails(false);
    }
  };

  const removeFile = async (fileId) => {
    if (extractingEmails) return; // Do not allow removing files while extraction is in progress
    const fileToRemove = uploadedFiles.find(f => f.id === fileId);
    if (fileToRemove && fileToRemove.file) {
      setActualFiles(prev => {
        const updatedActualFiles = prev.filter(f => f !== fileToRemove.file);

        // Email extraction removed - no longer extracting emails when files are removed
        // Clear emails if no files left
        if (updatedActualFiles.length === 0) {
          setExtractedEmails([]);
          setSelectedEmails([]);
        }

        return updatedActualFiles;
      });
    } else {
      // If no actual file reference, just check if we should clear emails
      if (uploadedFiles.length <= 1) {
        setExtractedEmails([]);
        setSelectedEmails([]);
      }
    }

    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    toast.info('File removed', { toastId: 'file-removed' });
  };

  // Toggle email selection
  const toggleEmailSelection = (emailId) => {
    setSelectedEmails(prev => {
      if (prev.includes(emailId)) {
        return prev.filter(id => id !== emailId);
      } else {
        return [...prev, emailId];
      }
    });
  };

  // Select all emails
  const selectAllEmails = () => {
    setSelectedEmails(extractedEmails.map(e => e.id));
  };

  // Deselect all emails
  const deselectAllEmails = () => {
    setSelectedEmails([]);
  };

  const toggleInviteRow = (rowId) => {
    setInviteRows(prev => prev.map(r => r.id === rowId ? { ...r, selected: !r.selected } : r));
  };

  const selectAllInviteRows = () => {
    setInviteRows(prev => prev.map(r => ({ ...r, selected: true })));
  };

  const deselectAllInviteRows = () => {
    setInviteRows(prev => prev.map(r => ({ ...r, selected: false })));
  };

  // Fetch avatar plan details when dropdown is opened
  const fetchAvatarPlan = async () => {
    // Only fetch if not already loaded
    if (avatarPlanDetails) {
      return;
    }

    setLoadingAvatarPlan(true);
    try {
      const planResponse = await avatarsAPI.getAvatarPlan();
      if (planResponse.status && planResponse.data) {
        setAvatarPlanDetails(planResponse.data);
        console.log('Avatar plan details fetched:', planResponse.data);
      }
    } catch (error) {
      console.error('Error fetching avatar plan:', error);
    } finally {
      setLoadingAvatarPlan(false);
    }
  };

  // Get dropdown options from API response
  const getDropdownOptions = () => {
    const options = [];

    // Always add "Send Invite" as the first option
    // If the backend requires plan_pricing_id even for send_invite, populate it when available.
    const sendInvitePricingId =
      avatarPlanDetails?.pricing_options?.find((opt) => Number(opt.plan_id) === 1)?.plan_pricing_id ?? null;
    options.push({
      value: 'send_invite',
      label: 'Send Invite',
      plan_id: 1, // plan_id 1 = Send Invitation
      plan_pricing_id: sendInvitePricingId
    });

    // Add options from API response if available
    if (avatarPlanDetails?.pricing_options && Array.isArray(avatarPlanDetails.pricing_options)) {
      avatarPlanDetails.pricing_options.forEach((option) => {
        const intervalName = option.interval_name || '';
        // Map interval names to proper labels
        let label = '';
        let value = '';
        if (intervalName === 'monthly') {
          label = 'Gift Month';
          value = 'gift_month';
        } else if (intervalName === 'yearly') {
          label = 'Gift Year';
          value = 'gift_year';
        } else {
          label = intervalName.charAt(0).toUpperCase() + intervalName.slice(1);
          value = `gift_${intervalName}`;
        }

        // Only add if not already in options
        if (!options.find(opt => opt.value === value)) {
          options.push({ value, label, plan_id: option.plan_id, plan_pricing_id: option.plan_pricing_id });
        }
      });
    } else {
      // Fallback to default options if API hasn't loaded yet
      options.push(
        { value: 'gift_month', label: 'Gift Month', plan_id: 2, plan_pricing_id: null },
        { value: 'gift_year', label: 'Gift Year', plan_id: 3, plan_pricing_id: null }
      );
    }

    return options;
  };

  const setInviteRowAction = (rowId, action) => {
    console.log(`=== setInviteRowAction called ===`);
    console.log('rowId:', rowId);
    console.log('newAction:', action);
    console.log('Current inviteRows before update:', inviteRows);

    // Find the selected option to get plan_id + plan_pricing_id
    let planPricingId = null;
    let planId = null;

    // First try from dropdown options
    const selectedOption = getDropdownOptions().find(opt => opt.value === action);
    planPricingId = selectedOption?.plan_pricing_id;
    planId = selectedOption?.plan_id ?? null;

    // If not found, try direct lookup from avatarPlanDetails
    // Note: send_invite doesn't need plan_pricing_id, it uses plan_id 1
    if (!planPricingId && avatarPlanDetails?.pricing_options) {
      if (action === 'gift_month') {
        const monthlyOption = avatarPlanDetails.pricing_options.find(opt => opt.interval_name === 'monthly');
        planPricingId = monthlyOption?.plan_pricing_id;
      } else if (action === 'gift_year') {
        const yearlyOption = avatarPlanDetails.pricing_options.find(opt => opt.interval_name === 'yearly');
        planPricingId = yearlyOption?.plan_pricing_id;
      }
      // send_invite uses plan_id 1, no plan_pricing_id needed
    }

    // Always guarantee plan_id for send_invite
    if (action === 'send_invite') {
      planId = 1;
    }

    console.log(`setInviteRowAction: action=${action}, planPricingId=${planPricingId}, selectedOption:`, selectedOption);

    setInviteRows(prev => {
      const updated = prev.map(r => {
        if (r.id === rowId) {
          console.log(`Updating action for row ${rowId} (${r.email}):`, {
            oldAction: r.action,
            newAction: action,
            email: r.email,
            planPricingId,
            fullRowBefore: r
          });
          const newRow = { ...r, action, plan_id: planId ?? r.plan_id ?? 1, plan_pricing_id: planPricingId };
          console.log(`New row after update:`, newRow);
          return newRow;
        }
        return r;
      });
      console.log('=== Updated inviteRows ===');
      console.log(updated);
      console.log('Updated rows actions:', updated.map(r => ({ email: r.email, action: r.action, plan_pricing_id: r.plan_pricing_id, id: r.id })));
      return updated;
    });
  };

  const removeInviteRow = (rowId) => {
    setInviteRows(prev => prev.filter(r => r.id !== rowId));
  };

  const updateInviteRowField = (rowId, field, value) => {
    setInviteRows(prev =>
      prev.map(r => (r.id === rowId ? { ...r, [field]: value } : r))
    );
  };

  const addBlankInviteRow = () => {
    const newRowId = `manual-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setInviteRows(prev => [
      ...prev,
      {
        id: newRowId,
        name: '',
        email: '',
        selected: true,
        action: 'send_invite', // Default to Send Invite
        plan_id: 1, // plan_id 1 = Send Invitation
        plan_pricing_id: null, // Send Invite doesn't need plan_pricing_id
        source: 'manual',
        isManual: true,
        isEditing: true,
      }
    ]);

    // Ensure the full list is shown when adding a new row
    if (inviteRows.length > 3 && !showAllInviteRows) {
      setShowAllInviteRows(true);
    }

    // Scroll to the new row after a brief delay to allow DOM update
    setTimeout(() => {
      const newRowElement = document.querySelector(`[data-row-id="${newRowId}"]`);
      if (newRowElement) {
        newRowElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        // Focus on the name input
        const nameInput = newRowElement.querySelector('.invite-row-name-input');
        if (nameInput) {
          nameInput.focus();
        }
      }
    }, 100);
  };

  // Send invite emails after avatar creation
  // This endpoint ONLY accepts emails that were extracted from the avatar creation (extracted_emails)
  // Flow: create avatar -> response includes extracted_emails -> user selects emails -> call this endpoint
  // cURL format:
  // curl -X POST "http://<host>/api/avatars/{avatar_id}/invite-emails" \
  //   -H "Content-Type: application/json" \
  //   -H "Authorization: Bearer <ACCESS_TOKEN>" \
  //   -d '{
  //     "selected_email_recipients": [
  //       {"email": "email1@example.com", "plan_id": 1},
  //       {"email": "email2@example.com", "plan_id": 2}
  //     ]
  //   }'
  const handleSendInviteEmails = async () => {
    // Allowed recipients: extracted from create-avatar response OR manually added via "+ Add someone".
    // Historical note: some rows use source === 'extract' (extracted_emails), others use source === 'create'.
    const selectedRows = inviteRows.filter((r) => r.selected);
    const allowedRows = selectedRows.filter(
      (r) => r.source === 'create' || r.source === 'extract' || r.source === 'manual' || r.isManual === true
    );

    if (!createdAvatarId) {
      toast.error('Invalid avatar ID. Please try creating the avatar again.', {
        toastId: 'invalid-avatar-id'
      });
      return;
    }

    if (selectedRows.length === 0) {
      toast.error('Please select at least one email to send invitations.', {
        toastId: 'no-emails-selected'
      });
      return;
    }

    if (allowedRows.length === 0) {
      toast.error(
        'Please select at least one valid recipient (from the list or add someone with "+ Add someone").',
        { toastId: 'invite-no-allowed-emails', autoClose: 5000 }
      );
      return;
    }

    // Validate selected rows have valid email addresses (only allowed rows are sent)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Validate avatar ID is a number
    const avatarIdNum = Number(createdAvatarId);
    if (!avatarIdNum || isNaN(avatarIdNum)) {
      toast.error('Invalid avatar ID. Please try creating the avatar again.', {
        toastId: 'invalid-avatar-id'
      });
      return;
    }

    setSendingInvites(true);

    // Declare selectedEmailRecipients outside try block so it's accessible in catch block
    let selectedEmailRecipients = [];

    try {
      // IMPORTANT: Only call plan API when needed.
      // If the default action is "Send Invite" for all selected rows, we do NOT need plan_pricing_id
      // and we should not call the plan API.
      const needsGiftPlanDetails = allowedRows.some((r) => (r.action || 'send_invite') !== 'send_invite');
      if (needsGiftPlanDetails) {
        try {
          await fetchAvatarPlan();
        } catch (e) {
          // Non-blocking; we'll validate plan_pricing_id for gift actions below.
          console.warn('Invite: failed to prefetch avatar plan details:', e);
        }
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidSelected = allowedRows.filter(r => !emailRegex.test((r.email || '').trim()));
      if (invalidSelected.length > 0) {
        toast.error('Please fix invalid/empty email addresses before sending invites.', {
          toastId: 'invite-invalid-emails',
          autoClose: 4000
        });
        return;
      }

      const selectedEmailAddresses = allowedRows.map(r => (r.email || '').trim());

      // Backend contract:
      // POST /api/avatars/{avatar_id}/invite-emails
      // { selected_email_recipients: [{ email, plan_id }, ...] }
      // plan_id is REQUIRED for every email (backend validation)
      // plan_id mapping as per backend specification:
      // - 1 = Send Invitation
      // - 2 = Gift Month
      // - 3 = Gift Year
      // Log all selectedRows before mapping to debug action values
      console.log('=== DEBUG: All selectedRows before mapping ===');
      console.log('selectedRows:', selectedRows);
      console.log('selectedRows actions:', selectedRows.map(r => ({ email: r.email, action: r.action, id: r.id })));
      console.log('Current inviteRows state:', inviteRows);

      selectedEmailRecipients = allowedRows.map((r) => {
        const email = (r.email || '').trim();
        const action = r.action || 'send_invite';

        // Log the action for debugging
        console.log(`[${email}] Mapping action:`, {
          action,
          rowId: r.id,
          fullRow: r,
          actionType: typeof r.action,
          actionValue: r.action,
          plan_pricing_id: r.plan_pricing_id,
          actionStringified: JSON.stringify(r.action)
        });

        // Get plan_id and plan_pricing_id from API response (plan API)
        // Always force plan_id=1 for send_invite so it never becomes null.
        let planId = action === 'send_invite' ? 1 : null;
        let planPricingId = null;

        // First try to get from dropdown options (which come from API)
        const dropdownOptions = getDropdownOptions();
        const selectedOption = dropdownOptions.find(opt => opt.value === action);

        if (selectedOption) {
          planId = selectedOption.plan_id;
          planPricingId = selectedOption.plan_pricing_id;
          console.log(`[${email}] Found option from dropdown - plan_id: ${planId}, plan_pricing_id: ${planPricingId}`);
        }

        // If not found in dropdown, try direct lookup from avatarPlanDetails or use defaults
        if (!planId) {
          if (action === 'send_invite') {
            planId = 1; // plan_id 1 = Send Invitation
            console.log(`[${email}] Using send_invite - plan_id: 1`);
          } else if (action === 'gift_month') {
            const monthlyOption = avatarPlanDetails?.pricing_options?.find(opt => opt.interval_name === 'monthly');
            if (monthlyOption) {
              planId = monthlyOption.plan_id;
              planPricingId = monthlyOption.plan_pricing_id;
              console.log(`[${email}] Found monthly option from API - plan_id: ${planId}, plan_pricing_id: ${planPricingId}`);
            } else {
              planId = 2; // Fallback: plan_id 2 = Gift Month
            }
          } else if (action === 'gift_year') {
            const yearlyOption = avatarPlanDetails?.pricing_options?.find(opt => opt.interval_name === 'yearly');
            if (yearlyOption) {
              planId = yearlyOption.plan_id;
              planPricingId = yearlyOption.plan_pricing_id;
              console.log(`[${email}] Found yearly option from API - plan_id: ${planId}, plan_pricing_id: ${planPricingId}`);
            } else {
              planId = 3; // Fallback: plan_id 3 = Gift Year
            }
          }
        }

        // Fallback: use stored values from row if available
        if (!planId && r.plan_id) {
          planId = r.plan_id;
        }
        if (!planPricingId && r.plan_pricing_id) {
          planPricingId = r.plan_pricing_id;
        }

        // Special case: send_invite often needs plan_pricing_id=plan_id(1) pricing option.
        if ((action === 'send_invite' || planId === 1) && (planPricingId === null || planPricingId === undefined)) {
          const sendInviteOpt = avatarPlanDetails?.pricing_options?.find(opt => Number(opt.plan_id) === 1);
          if (sendInviteOpt?.plan_pricing_id !== undefined && sendInviteOpt?.plan_pricing_id !== null) {
            planPricingId = sendInviteOpt.plan_pricing_id;
          }
        }

        // Final fallback: use hardcoded values if API data not available (should not happen if API is called)
        if (!planId) {
          planId = 2; // Default plan_id from API response
          console.warn(`[${email}] Using fallback plan_id: ${planId} for action: ${action}`);
        }

        // Ensure plan_id is always a number (required by backend)
        planId = Number(planId);

        // Validate plan_id is a valid number
        if (isNaN(planId) || planId === null || planId === undefined) {
          console.error(`[${email}] ERROR: Invalid plan_id for action '${action}'. Defaulting to 2.`);
          planId = 2;
        }

        const recipient = { email, plan_id: planId };

        // Add name if available (for manually added entries)
        if (r.name && r.name.trim()) {
          recipient.name = r.name.trim();
        }

        // Add plan_pricing_id when available.
        // If backend requires it and it's missing, we'll catch that in validation below.
        if (planPricingId !== undefined && planPricingId !== null && planPricingId !== '') {
          recipient.plan_pricing_id = Number(planPricingId);
          console.log(`[${email}] ✓ Added plan_pricing_id: ${recipient.plan_pricing_id} for action: ${action}`);
        }

        // Double-check plan_id is included
        if (recipient.plan_id === undefined || recipient.plan_id === null || isNaN(recipient.plan_id)) {
          console.error(`[${email}] ERROR: plan_id is missing or invalid for recipient:`, recipient);
          recipient.plan_id = 2; // Force default
        }

        // Log the final recipient for debugging
        console.log(`[${email}] Final recipient:`, recipient);

        return recipient;
      });

      // Only require plan_pricing_id for gift plans (monthly/yearly).
      // For default "Send Invite" (plan_id=1), allow sending without plan_pricing_id.
      const missingPlanPricing = selectedEmailRecipients.filter(r =>
        Number(r.plan_id) !== 1 &&
        (r.plan_pricing_id === undefined || r.plan_pricing_id === null || isNaN(r.plan_pricing_id))
      );
      if (missingPlanPricing.length > 0) {
        console.error('Invite blocked: missing plan_pricing_id for recipients:', missingPlanPricing);
        toast.error(
          'Unable to send gift invites right now because plan details are missing (plan_pricing_id). Please open the action dropdown once (to load plan details) and try again.',
          { toastId: 'invite-missing-plan-pricing', autoClose: 6000 }
        );
        return;
      }

      console.log('=== DEBUG: Final selectedEmailRecipients ===');
      console.log(JSON.stringify(selectedEmailRecipients, null, 2));
      console.log('Recipients count:', selectedEmailRecipients.length);
      console.log('Allowed rows count (create/extract/manual):', allowedRows.length);
      console.log('Each recipient:', selectedEmailRecipients.map(r => ({
        email: r.email,
        name: r.name || 'N/A',
        plan_id: r.plan_id,
        plan_id_type: typeof r.plan_id,
        source: allowedRows.find(a => (a.email || '').trim() === r.email)?.source || 'create'
      })));

      // Validate all recipients have plan_id before sending
      const recipientsWithoutPlanId = selectedEmailRecipients.filter(r =>
        r.plan_id === undefined || r.plan_id === null || isNaN(r.plan_id)
      );

      if (recipientsWithoutPlanId.length > 0) {
        console.error('ERROR: Recipients missing plan_id:', recipientsWithoutPlanId);
        toast.error(`Error: ${recipientsWithoutPlanId.length} recipient(s) are missing plan_id. Please try again.`, {
          toastId: 'missing-plan-id-error',
          autoClose: 5000
        });
        return;
      }

      // Log the payload before sending
      if (process.env.NODE_ENV === 'development') {
        const planIdCounts = selectedEmailRecipients.reduce((acc, r) => {
          acc[r.plan_id] = (acc[r.plan_id] || 0) + 1;
          return acc;
        }, {});

        console.log('Invite emails payload (before API call):', {
          selected_email_recipients: selectedEmailRecipients,
          plan_id_counts: planIdCounts,
          mapping: {
            'Send Invitation': 1,
            'Gift Month': 2,
            'Gift Year': 3
          },
          validation: {
            allHavePlanId: selectedEmailRecipients.every(r => typeof r.plan_id === 'number' && !isNaN(r.plan_id)),
            recipientsWithPlanId: selectedEmailRecipients.map(r => ({ email: r.email, plan_id: r.plan_id, type: typeof r.plan_id }))
          }
        });
      }

      // Validate: Check if we're using plan IDs that might not exist
      const usedPlanIds = [...new Set(selectedEmailRecipients.map(r => r.plan_id))];
      if (usedPlanIds.some(id => id === 2 || id === 3)) {
        console.warn('Warning: Using plan_id 2 or 3. These may not exist in the backend. Plan ID 1 is confirmed to exist (from registration).');
      }

      const response = await avatarsAPI.sendInviteEmails(createdAvatarId, {
        selected_email_recipients: selectedEmailRecipients
      });

      // Check if response indicates success
      const isSuccess = response.status === true || response.status_code === 200 || response.status_code === 201;

      if (isSuccess) {
        const manuallyAddedCount = allowedRows.filter(r => r.source === 'manual' || r.isManual).length;
        const extractedCount = allowedRows.filter(r => r.source !== 'manual' && !r.isManual).length;
        let successMessage = `Invitations sent successfully to ${selectedEmailAddresses.length} recipient(s)!`;
        if (manuallyAddedCount > 0) {
          successMessage += ` (${manuallyAddedCount} manually added, ${extractedCount} from uploaded content)`;
        }
        toast.success(successMessage, {
          toastId: 'invites-sent-success'
        });

        // Clear avatars cache
        sessionStorage.removeItem('avatars_fetched');
        sessionStorage.removeItem('avatars_data');
        sessionStorage.removeItem('has_avatars');

        // Call invitee members API after sending invites
        const currentUserId = userId || await getUserId();
        if (currentUserId) {
          try {
            await projectsAPI.getInviteeMembers(currentUserId);
            console.log('Invitee members fetched after sending invites');
          } catch (inviteeError) {
            console.warn('Failed to fetch invitee members after sending invites:', inviteeError);
            // Don't block navigation if this fails
          }
        }

        // Close modal and redirect to chat page
        setShowInviteModal(false);
        setInvitePromptEmails([]);
        setInviteRows([]);
        setCreatedAvatarId(null);

        setTimeout(() => {
          navigate('/chat', { replace: true });
        }, 1500);
      } else {
        throw new Error('Failed to send invitations');
      }
    } catch (error) {
      console.error('Error sending invite emails:', error);
      console.error('Error details:', {
        status: error.status || error.statusCode,
        message: error.message,
        response: error.response,
        request: error.request
      });

      let errorMessage = error.message || 'Failed to send invitations. Please try again.';

      // Extract detailed error from backend response
      if (error.response) {
        // Try multiple paths for error message
        const backendError =
          error.response.detail ||
          error.response.error?.msg ||
          error.response.error?.error ||
          error.response.error ||
          error.response.message ||
          error.response.msg;

        if (backendError) {
          if (Array.isArray(backendError)) {
            // Handle validation errors array (FastAPI format)
            errorMessage = backendError.map(err =>
              err.msg || err.message || JSON.stringify(err)
            ).join(', ');
          } else if (typeof backendError === 'string') {
            errorMessage = backendError;
          } else if (typeof backendError === 'object') {
            errorMessage = JSON.stringify(backendError);
          }
        }

        // Log full response for debugging
        console.error('Full backend error response:', JSON.stringify(error.response, null, 2));
      }

      // Log payload that was sent for debugging plan_id issues
      if (errorMessage.toLowerCase().includes('plan_id') || errorMessage.toLowerCase().includes('invalid plan') || errorMessage.toLowerCase().includes('inactive')) {
        console.error('Plan ID error detected. Payload sent:', JSON.stringify(selectedEmailRecipients, null, 2));
        const sentPlanIds = selectedEmailRecipients.map(r => r.plan_id);
        const uniquePlanIds = [...new Set(sentPlanIds)];
        console.error('Plan IDs sent in request:', uniquePlanIds);
        console.error('Expected plan IDs: 2 (Gift Month), 3 (Gift Year)');
        console.error('Mapping: gift_month=2, gift_year=3');

        // Show which actions were mapped to which plan IDs
        const actionMapping = selectedRows.map(r => ({
          email: r.email,
          action: r.action || 'gift_month',
          plan_id: r.action === 'gift_month' ? 2 : r.action === 'gift_year' ? 3 : 2
        }));
        console.error('Action to Plan ID mapping:', actionMapping);

        // Update error message to be more helpful
        if (errorMessage.toLowerCase().includes('inactive') || errorMessage.toLowerCase().includes('invalid')) {
          errorMessage = `Invalid plan_id: The plan IDs (${uniquePlanIds.join(', ')}) do not exist or are inactive in the backend. Please ensure plans with IDs 1, 2, and 3 are created and active in the database.`;
        }
      }

      if (error.status === 401 || error.statusCode === 401) {
        // Handle 401 Unauthorized - token expired or invalid
        const errorDetail = error.response?.error?.message || error.response?.error?.code || error.message;
        if (errorDetail && (errorDetail.toLowerCase().includes('token') || errorDetail.toLowerCase().includes('unauthorized'))) {
          // Clear invalid tokens
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          sessionStorage.clear();
          errorMessage = 'Your session has expired. Please sign in again.';
          setTimeout(() => {
            navigate('/signin');
          }, 2000);
        } else {
          errorMessage = errorDetail || 'Unauthorized. Please sign in again.';
          setTimeout(() => {
            navigate('/signin');
          }, 2000);
        }
      } else if (error.status === 404 || error.statusCode === 404) {
        errorMessage = 'Avatar not found. Please try creating the avatar again.';
      } else if (error.status >= 500 || error.statusCode >= 500) {
        // Show backend error details if available
        const serverError = error.response?.detail || error.response?.error || error.response?.message;
        if (serverError) {
          errorMessage = `Server error: ${typeof serverError === 'string' ? serverError : JSON.stringify(serverError)}`;
        } else {
          errorMessage = `Server error (${error.status || error.statusCode}). Please check the browser console for details.`;
        }
      }

      toast.error(errorMessage, {
        toastId: (error.status === 401 || error.statusCode === 401) ? 'session-expired' : 'invites-send-error',
        autoClose: 6000
      });
    } finally {
      setSendingInvites(false);
    }
  };

  // Skip sending invites and go to chat
  const handleSkipInvites = () => {
    // Clear avatars cache
    sessionStorage.removeItem('avatars_fetched');
    sessionStorage.removeItem('avatars_data');
    sessionStorage.removeItem('has_avatars');

    // Close modal and redirect
    setShowInviteModal(false);
    setInvitePromptEmails([]);
    setInviteRows([]);
    setCreatedAvatarId(null);

    navigate('/chat', { replace: true });
  };

  // Note: Email sending is now handled automatically by the backend when creating avatar
  // The selected_email_recipients are sent as part of the avatar creation request

  // Check if a file is a media file (image, video, or audio) that should be filtered out
  // Note: Archive files (zip, rar, 7z) are NOT filtered out
  const isMediaFile = (file) => {
    if (!file || !file.name) return false;

    const ext = file.name.split('.').pop().toLowerCase();
    const mimeType = file.type ? file.type.toLowerCase() : '';

    // Archive extensions - DO NOT filter these out
    const archiveExts = ['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz'];
    if (archiveExts.includes(ext)) {
      return false; // Keep archive files
    }

    // Image extensions
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico', 'tiff', 'tif', 'heic', 'heif', 'raw', 'cr2', 'nef', 'orf', 'sr2', 'psd', 'ai', 'eps'];
    // Video extensions
    const videoExts = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'm4v', 'mpg', 'mpeg', '3gp', 'ogv', 'ts', 'mts', 'm2ts'];
    // Audio extensions
    const audioExts = ['mp3', 'wav', 'flac', 'aac', 'ogg', 'wma', 'm4a', 'opus', 'amr', 'aiff', 'au', 'ra', 'rm', 'wv', 'ape', 'dsd'];

    // Check by extension
    if (imageExts.includes(ext) || videoExts.includes(ext) || audioExts.includes(ext)) {
      return true;
    }

    // Check by MIME type
    if (mimeType) {
      if (mimeType.startsWith('image/') || mimeType.startsWith('video/') || mimeType.startsWith('audio/')) {
        return true;
      }
    }

    return false;
  };

  // Filter out media files and return only text-based files
  const filterTextFiles = (files) => {
    if (!files || files.length === 0) return [];

    const textFiles = [];
    const filteredOut = [];

    files.forEach(file => {
      if (isMediaFile(file)) {
        filteredOut.push(file);
      } else {
        textFiles.push(file);
      }
    });

    // Removed toast notification for filtered media files - silent filtering

    return textFiles;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getFileType = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const nameLower = filename.toLowerCase();

    // Check for email-related archives (gmail, email, mail)
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
      if (nameLower.includes('gmail') || nameLower.includes('email') || nameLower.includes('mail')) {
        return 'email-archive';
      }
      // Check for message-related archives (imessage, message, chat, sms, whatsapp)
      if (nameLower.includes('imessage') || nameLower.includes('message') || nameLower.includes('chat') || nameLower.includes('sms') || nameLower.includes('whatsapp')) {
        return 'message-archive';
      }
      return 'archive';
    }
    if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) return 'document';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'image';
    return 'file';
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'email-archive':
        // Envelope icon for email archives
        return (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'message-archive':
        // Speech bubble icon for message archives
        return (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        );
      case 'archive':
        return (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
          </svg>
        );
      case 'document':
        return (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'image':
        return (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      default:
        return (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
    }
  };

  // Get user ID - use cached value or fetch if not available
  const getUserId = async () => {
    // Return cached user ID if available
    if (userId) {
      console.log('CreateAvatarPage: Using cached userId:', userId);
      return userId;
    }

    // Check sessionStorage first (might be set by Navbar or LandingPage)
    const userFetchedInSession = sessionStorage.getItem('user_fetched');
    const storedUserId = sessionStorage.getItem('user_id');

    if (userFetchedInSession && storedUserId) {
      const cachedUserId = parseInt(storedUserId, 10);
      setUserId(cachedUserId); // Update local state
      console.log('CreateAvatarPage: Using userId from sessionStorage:', cachedUserId);
      return cachedUserId;
    }

    // If not cached and not already fetching, fetch it once
    if (!userFetchedRef.current && !userFetchedInSession) {
      await fetchUserOnce();
      return userId;
    }

    // If currently fetching or already fetched in session, return null or wait
    // The fetchUserOnce function will handle the sessionStorage check
    return null;
  };

  // Check if current plan requires payment
  const requiresPayment = () => {
    const current = getCurrentSelectedPlan();
    if (!current) return false;
    return Number(current.price_usd || 0) > 0;
  };

  const hasRestoredFilePlaceholdersOnly = () => {
    return (
      Array.isArray(uploadedFiles) &&
      uploadedFiles.length > 0 &&
      (!actualFiles || actualFiles.length === 0) &&
      uploadedFiles.some((f) => Boolean(f?.isRestored))
    );
  };

  const clearPaymentDraft = () => {
    const currentUid = getCurrentSessionUserId();
    if (!currentUid) return;
    sessionStorage.removeItem(getPaymentDraftKey(currentUid));
  };

  const persistActivatedPlanForCurrentUser = (activatedPlan) => {
    try {
      const currentUid = getCurrentSessionUserId();
      if (!currentUid || !activatedPlan?.avatar_plan_id || !activatedPlan?.plan_interval) return;
      sessionStorage.setItem(getActivatedPlanKey(currentUid), JSON.stringify(activatedPlan));
    } catch (err) {
      console.warn('Failed to persist activated plan:', err);
    }
  };

  const savePaymentDraft = () => {
    try {
      const currentUid = getCurrentSessionUserId();
      if (!currentUid) return;
      const draft = {
        user_id: currentUid,
        firstName: (getValues('firstName') || '').trim(),
        lastName: (getValues('lastName') || '').trim(),
        avatarType,
        selectedRelation,
        selectedPlanId,
        billingPeriod,
        autoRenew: Boolean(autoRenew),
        uploadedFiles: (uploadedFiles || []).map((file, idx) => ({
          id: file?.id ?? `file-${idx}`,
          name: file?.name || '',
          size: Number(file?.size || 0),
          type: file?.type || '',
        })),
        savedAt: new Date().toISOString(),
      };
      sessionStorage.setItem(getPaymentDraftKey(currentUid), JSON.stringify(draft));
    } catch (err) {
      console.warn('Failed to save create-avatar payment draft:', err);
    }
  };

  // Process payment using backend PayPal order flow (create-order -> PayPal approve -> /payment/success capture)
  const processPayment = async () => {
    const current = getCurrentSelectedPlan();
    if (!current || !current.avatar_plan_id) {
      toast.error('Please select a valid plan first.');
      return;
    }

    // Free trial / zero-price plan: no PayPal redirect required.
    if (Number(current.price_usd || 0) <= 0) {
      setProcessingPayment(true);
      try {
        const captureRes = await paymentsAPI.capturePaypalOrder({
          order_id: `FREE-${Date.now()}`,
          avatar_plan_id: current.avatar_plan_id,
          plan_interval: current.interval,
          auto_renew: Boolean(autoRenew),
        });
        const payload = captureRes?.data || captureRes || {};
        const activated = {
          avatar_plan_id: current.avatar_plan_id,
          plan_interval: current.interval,
          auto_renew: Boolean(autoRenew),
          subscription_id: payload?.subscription_id || null,
          invoice_id: payload?.invoice_id || null,
          invoice_download_url: payload?.invoice_download_url || null,
        };
        setPaymentActivatedPlan(activated);
        persistActivatedPlanForCurrentUser(activated);
        sessionStorage.setItem('paypal_capture_result', JSON.stringify(activated));
        toast.success('Free plan activated successfully.');
        setShowPaymentModal(false);
      } catch (error) {
        console.error('Free-plan activation failed:', error);
        toast.error(error?.message || 'Unable to activate free plan. Please try again.');
      } finally {
        setProcessingPayment(false);
      }
      return;
    }

    setProcessingPayment(true);
    try {
      // Persist draft before redirecting to PayPal.
      savePaymentDraft();

      const createOrderRes = await paymentsAPI.createPaypalOrder({
        avatar_plan_id: current.avatar_plan_id,
        plan_interval: current.interval,
      });

      const orderId = createOrderRes?.data?.order_id || createOrderRes?.order_id || null;
      const approveUrl = createOrderRes?.data?.approve_url || createOrderRes?.approve_url || null;
      if (!orderId || !approveUrl) {
        throw new Error('Invalid create-order response. Missing order_id or approve_url.');
      }

      // Persist checkout context for PaymentSuccess capture and optional restore.
      localStorage.setItem('paypal_checkout_context', JSON.stringify({
        order_id: orderId,
        avatar_plan_id: current.avatar_plan_id,
        plan_interval: current.interval,
        auto_renew: Boolean(autoRenew),
        expected_amount_usd: createOrderRes?.data?.expected_amount_usd ?? createOrderRes?.expected_amount_usd ?? null,
        started_from: '/create-avatar',
        created_at: new Date().toISOString(),
      }));

      window.location.href = approveUrl;
    } catch (error) {
      console.error('Payment processing error:', error);
      toast.error(error?.message || 'Payment processing failed. Please try again.', {
        toastId: 'payment-error'
      });
    } finally {
      setProcessingPayment(false);
    }
  };

  // Create avatar after successful payment
  const createAvatarAfterPayment = async (avatarData) => {
    setLoading(true);

    try {
      const response = await avatarsAPI.createAvatar(
        avatarData.avatarData,
        avatarData.actualFiles,
        avatarData.selectedEmailAddresses
      );

      // Handle success
      const isSuccess = (response.status === true || response.status_code === 201 || response.status_code === 200) && response.data;

      if (isSuccess) {
        clearPaymentDraft();
        const newAvatar = response.data;

        // Call avatar plan API after avatar creation
        try {
          const planResponse = await avatarsAPI.getAvatarPlan();
          if (planResponse.status && planResponse.data) {
            setAvatarPlanDetails(planResponse.data);
            console.log('Avatar plan details fetched after avatar creation:', planResponse.data);

            // Update existing inviteRows with plan_pricing_id if they don't have it
            // Use planResponse.data directly instead of getDropdownOptions() to avoid closure issues
            if (inviteRows.length > 0 && planResponse.data.pricing_options) {
              setInviteRows(prev => prev.map(row => {
                if (!row.plan_pricing_id) {
                  // Find matching pricing option based on action
                  const action = row.action || 'send_invite';
                  let matchingPricingOption = null;

                  if (action === 'gift_month') {
                    matchingPricingOption = planResponse.data.pricing_options.find(opt => opt.interval_name === 'monthly');
                  } else if (action === 'gift_year') {
                    matchingPricingOption = planResponse.data.pricing_options.find(opt => opt.interval_name === 'yearly');
                  }

                  if (matchingPricingOption?.plan_pricing_id) {
                    console.log(`Updating row ${row.id} (${row.email}) with plan_pricing_id:`, matchingPricingOption.plan_pricing_id);
                    return { ...row, plan_pricing_id: matchingPricingOption.plan_pricing_id };
                  }
                }
                return row;
              }));
            }
          }
        } catch (error) {
          console.error('Error fetching avatar plan after avatar creation:', error);
        }

        toast.success(`Avatar "${newAvatar.first_name} ${newAvatar.last_name}" created successfully!`, {
          toastId: 'avatar-creation-success'
        });

        // Check if response contains invite_prompt or extracted_emails
        // Flow: create avatar -> response includes invite_prompt/extracted_emails -> show popup for invitation
        const invitePrompt = newAvatar.invite_prompt || response.data?.invite_prompt;
        const extractedEmailsFromResponse = newAvatar.extracted_emails || response.data?.extracted_emails || newAvatar.prediction_summary?.extracted_emails;

        // Prefer invite_prompt.candidates (more structured) over extracted_emails
        let formattedInviteEmails = [];

        if (invitePrompt && invitePrompt.candidates && Array.isArray(invitePrompt.candidates)) {
          // Use invite_prompt.candidates (has email, label, checked)
          formattedInviteEmails = invitePrompt.candidates.map((candidate, index) => {
            const emailAddress = (candidate.email || '').trim();
            if (!emailAddress) return null;
            return {
              id: Date.now() + index + Math.random(),
              email: emailAddress,
              name: candidate.label || candidate.name || emailAddress.split('@')[0] || 'Unknown',
              selected: candidate.checked !== false // Default to true if not specified
            };
          }).filter(email => email !== null);
        } else if (extractedEmailsFromResponse && Array.isArray(extractedEmailsFromResponse) && extractedEmailsFromResponse.length > 0) {
          // Fallback to extracted_emails (array of strings or objects)
          formattedInviteEmails = extractedEmailsFromResponse.map((emailItem, index) => {
            if (typeof emailItem === 'string') {
              const emailAddress = emailItem.trim();
              if (!emailAddress) return null;
              const nameFromEmail = emailAddress.split('@')[0];
              return {
                id: Date.now() + index + Math.random(),
                email: emailAddress,
                name: nameFromEmail.charAt(0).toUpperCase() + nameFromEmail.slice(1),
                selected: true
              };
            }
            const emailAddress = (emailItem.email || emailItem.email_address || '').trim();
            if (!emailAddress) return null;
            return {
              id: Date.now() + index + Math.random(),
              email: emailAddress,
              name: emailItem.name || emailItem.full_name || emailItem.label || emailAddress.split('@')[0] || 'Unknown',
              selected: emailItem.checked !== false
            };
          }).filter(email => email !== null);
        } else if (avatarData.extractedEmails && avatarData.extractedEmails.length > 0) {
          // Use emails from extract-emails API (stored in pendingAvatarData before payment)
          formattedInviteEmails = avatarData.extractedEmails.map(e => ({ ...e, selected: e.selected !== false }));
        }

        formattedInviteEmails = dedupeEmailObjects(formattedInviteEmails);
        formattedInviteEmails = formattedInviteEmails.filter(e => !isPlaceholderInviteEntry(e.email, e.name));

        if (formattedInviteEmails.length > 0) {
          // Store avatar ID and show invite modal
          setCreatedAvatarId(newAvatar.id);
          setInvitePromptEmails(formattedInviteEmails);
          // Initialize inviteRows with default action (Send Invite) for each row
          const initialInviteRows = formattedInviteEmails.map(e => ({
            ...e,
            selected: e.selected !== false,
            action: 'send_invite', // Default to Send Invite
            plan_id: 1, // plan_id 1 = Send Invitation
            plan_pricing_id: null // Send Invite doesn't need plan_pricing_id
          }));
          setInviteRows(initialInviteRows);
          if (process.env.NODE_ENV === 'development') {
            console.log('Initialized inviteRows:', initialInviteRows);
          }
          setShowInviteModal(true);
          setShowAllInviteRows(false); // Reset to collapsed view
          setLoading(false);
          setPendingAvatarData(null);
          // Don't redirect yet - wait for user to select emails and send invites
          return;
        }
        // No emails extracted from documents, but user had uploaded files — still show invite screen so they can add people manually
        const hadUploadedFiles = avatarData.actualFiles && avatarData.actualFiles.length > 0;
        if (hadUploadedFiles) {
          setCreatedAvatarId(newAvatar.id);
          setInvitePromptEmails([]);
          setInviteRows([]);
          setShowInviteModal(true);
          setShowAllInviteRows(false);
          setLoading(false);
          setPendingAvatarData(null);
          if (process.env.NODE_ENV === 'development') {
            console.log('No emails extracted from uploads (after payment); showing invite modal for manual add.');
          }
          return;
        }

        // Clear avatars cache
        sessionStorage.removeItem('avatars_fetched');
        sessionStorage.removeItem('avatars_data');
        sessionStorage.removeItem('has_avatars');

        // Call invitee members API after avatar creation
        const currentUserId = userId || await getUserId();
        if (currentUserId) {
          try {
            await projectsAPI.getInviteeMembers(currentUserId);
            console.log('Invitee members fetched after avatar creation (after payment)');
          } catch (inviteeError) {
            console.warn('Failed to fetch invitee members after avatar creation:', inviteeError);
            // Don't block navigation if this fails
          }
        }

        // Redirect to chat page
        setTimeout(() => {
          navigate('/chat', { replace: true });
        }, 2000);
      } else {
        throw new Error('Avatar creation failed after payment');
      }
    } catch (error) {
      console.error('Avatar creation error after payment:', error);
      toast.error('Failed to create avatar after payment. Please contact support.', {
        toastId: 'avatar-creation-error-after-payment'
      });
    } finally {
      setLoading(false);
      setPendingAvatarData(null);
    }
  };

  const onSubmit = async (data) => {
    // Validate that relation is selected
    if (!selectedRelation) {
      toast.error('Please select a relation.');
      setLoading(false);
      return;
    }

    // Validate that files are uploaded
    if (!actualFiles || actualFiles.length === 0) {
      if (hasRestoredFilePlaceholdersOnly()) {
        toast.error('Please re-upload your files. Previous upload list is shown for reference only.');
      } else {
        toast.error('Please upload at least one file to create an avatar.');
      }
      setLoading(false);
      return;
    }
    console.log('CreateAvatarPage: Submitting with files:', {
      count: actualFiles.length,
      files: actualFiles.map(f => ({ name: f.name, size: f.size, type: f.type }))
    });

    // Validate total file size before submission
    const MAX_TOTAL_SIZE = 5 * 1024 * 1024 * 1024; // 5 GB in bytes
    const totalSize = actualFiles.reduce((total, file) => total + file.size, 0);

    if (totalSize > MAX_TOTAL_SIZE) {
      const totalGB = (totalSize / (1024 * 1024 * 1024)).toFixed(2);
      toast.error(`Total file size (${totalGB} GB) exceeds the 5 GB limit. Please remove some files.`, {
        toastId: 'file-size-limit-submit',
        autoClose: 5000
      });
      return;
    }

    // Get user ID - use cached value if available
    const currentUserId = userId || await getUserId();
    if (!currentUserId) {
      toast.error('Unable to get user information. Please sign in again.');
      navigate('/signin');
      return;
    }

    // Check avatar limit against max_avatars
    try {
      setLoading(true);
      const avatarsResponse = await avatarsAPI.getAvatars(currentUserId);
      const currentAvatars = avatarsResponse.data?.avatars || avatarsResponse.data || [];
      const userPlan = userData?.subscription?.plan || userData?.plan;

      const maxAvatars = userPlan?.max_avatars;
      if (typeof maxAvatars === 'number') {
        if (currentAvatars.length >= maxAvatars) {
          toast.error(`You have reached the maximum number of avatars (${maxAvatars}) for your plan. Please upgrade to create more.`, {
            toastId: 'max-avatars-submit-limited',
            autoClose: 6000
          });
          setLoading(false);
          return;
        }
      } else {
        // Fallback max_avatars check
        const currentPlanName = userPlan?.name || '';
        const isFreePlan = !currentPlanName || currentPlanName.toLowerCase().includes('free');
        if (isFreePlan && currentAvatars.length >= 2) {
          toast.error('You are not allowed to create a new avatar. Please upgrade your plan to create multiple avatars.');
          setLoading(false);
          return;
        }
      }
    } catch (err) {
      console.warn('Failed to check avatar limits:', err);
    } finally {
      // Don't disable loading unconditionally to avoid flashing loading states if validation passes
    }

    // Scroll to top immediately when Create Avatar is clicked
    window.scrollTo({ top: 0, behavior: 'auto' });
    // Also scroll document element to ensure we reach absolute top
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }

    // Start BOTH calls together:
    // - create-avatar (main)
    // - extract-emails (for invite fallback)
    let extractEmailsPromise = null;
    if (actualFiles.length > 0) {
      setExtractingEmails(true);
      extractEmailsPromise = avatarsAPI
        .extractEmails(actualFiles, currentUserId)
        .then((extractResponse) => {
          let emailArray = [];
          if (extractResponse?.emails && Array.isArray(extractResponse.emails)) {
            emailArray = extractResponse.emails;
          } else if (extractResponse?.data?.emails && Array.isArray(extractResponse.data.emails)) {
            emailArray = extractResponse.data.emails;
          } else if (Array.isArray(extractResponse?.data)) {
            emailArray = extractResponse.data;
          } else if (Array.isArray(extractResponse)) {
            emailArray = extractResponse;
          }

          const baseId = Date.now();
          return (emailArray || [])
            .map((emailItem, index) => {
              if (typeof emailItem === 'string') {
                const emailAddress = emailItem.trim();
                if (!emailAddress) return null;
                const nameFromEmail = emailAddress.split('@')[0];
                return {
                  id: `extract-${baseId}-${index}-${Math.random().toString(36).slice(2, 9)}`,
                  email: emailAddress,
                  name: nameFromEmail.charAt(0).toUpperCase() + nameFromEmail.slice(1),
                  selected: true
                };
              }
              const emailAddress = (emailItem.email || emailItem.email_address || '').trim();
              if (!emailAddress) return null;
              return {
                id: `extract-${baseId}-${index}-${Math.random().toString(36).slice(2, 9)}`,
                email: emailAddress,
                name: emailItem.name || emailItem.full_name || emailItem.label || emailAddress.split('@')[0] || 'Unknown',
                selected: emailItem.checked !== false
              };
            })
            .filter(Boolean);
        })
        .catch((err) => {
          console.error('Email extraction failed (non-blocking):', err);
          return [];
        })
        .finally(() => {
          setExtractingEmails(false);
        });
    }

    // Create avatar immediately; if it needs invite emails and the response doesn't include them,
    // we'll fallback to extract-emails once it finishes.
    proceedWithAvatarCreation(data, [], extractEmailsPromise);
  };

  // Proceed with avatar creation (extractedEmailsFromApi = emails from extract-emails API for invite modal)
  const proceedWithAvatarCreation = async (formData, extractedEmailsFromApi = [], extractedEmailsPromise = null) => {
    // Use provided form data
    const data = formData;
    if (!data) {
      toast.error('Form data is missing. Please try again.');
      return;
    }

    // Get user ID - use cached value if available
    const currentUserId = userId || await getUserId();
    if (!currentUserId) {
      toast.error('Unable to get user information. Please sign in again.');
      navigate('/signin');
      return;
    }

    // Find the selected avatar type to get avatar_type_id
    let avatarTypeId = 1; // Default to 1 (for myself)
    const selectedAvatarType = avatarPlans.find(avatarTypeItem =>
      normalizeAvatarTypeName(avatarTypeItem.name) === normalizeAvatarTypeName(avatarType)
    );

    if (selectedAvatarType?.name) {
      // Backend /api/avatars currently supports only:
      // 1 = for myself, 2 = for a loved one.
      // Any custom paid family-like type should map to loved one for avatar creation flow.
      const normalizedTypeName = normalizeAvatarTypeName(selectedAvatarType.name);
      avatarTypeId = normalizedTypeName === 'for myself' ? 1 : 2;
    } else {
      // Fallback to UI-selected type
      avatarTypeId = avatarType === 'for myself' || avatarType.toLowerCase().includes('myself') ? 1 : 2;
    }

    // Get selected email addresses (empty array - no emails from documents)
    const selectedEmailAddresses = [];

    const avatarData = {
      first_name: data.firstName,
      last_name: data.lastName,
      avatar_type_id: avatarTypeId,
      user_id: currentUserId
    };

    // Add relation if selected (using value string from API)
    if (selectedRelation) {
      avatarData.relation = selectedRelation; // Use the value string (e.g., "father", "mother", "self")
    }

    // Add plan_id if selected (for loved one avatars)
    if (selectedPlanId) {
      avatarData.plan_id = selectedPlanId;
    } else if (avatarTypeId === 1) {
      // If "for myself" and no plan selected (default state), try to find the free/default plan
      const myselfType = avatarPlans.find(t => normalizeAvatarTypeName(t.name) === 'for myself');
      if (myselfType && myselfType.plans && myselfType.plans.length > 0) {
        avatarData.plan_id = myselfType.plans[0].id;
      }
    }

    // Always add plan_interval for loved one avatars (avatar_type_id=2)
    // API requires plan_interval for avatar_type_id=2, even if plan_id is provided
    if (avatarTypeId === 2) {
      // Use billingPeriod if available, otherwise default to 'yearly'
      const interval = billingPeriod === 'yearly' ? 'yearly' : (billingPeriod === 'monthly' ? 'monthly' : 'yearly');
      avatarData.plan_interval = interval;
    }



    // For free plans, create avatar directly
    setLoading(true);

    try {
      // Log the final avatarData before sending
      console.log('CreateAvatarPage: Final avatarData before API call:', avatarData);

      // Send avatar data, files, and selected email recipients to API
      const response = await avatarsAPI.createAvatar(avatarData, actualFiles, selectedEmailAddresses);

      // Handle API response structure: { status: true, status_code: 201, data: {...} }
      // Check for both status (boolean) and status_code (201) as success indicators
      const isSuccess = (response.status === true || response.status_code === 201 || response.status_code === 200) && response.data;

      if (isSuccess) {
        clearPaymentDraft();
        const newAvatar = response.data;
        console.log('CreateAvatarPage: Avatar created successfully:', {
          avatar_id: newAvatar.id,
          first_name: newAvatar.first_name,
          last_name: newAvatar.last_name,
          user_id: newAvatar.user_id,
          uploaded_files_count: newAvatar.uploaded_files?.length || 0,
          total_files_size: newAvatar.total_files_size || 0,
          status_code: response.status_code
        });

        // Call avatar plan API after avatar creation
        try {
          const planResponse = await avatarsAPI.getAvatarPlan();
          if (planResponse.status && planResponse.data) {
            setAvatarPlanDetails(planResponse.data);
            console.log('Avatar plan details fetched after avatar creation:', planResponse.data);

            // Update existing inviteRows with plan_pricing_id if they don't have it
            // Use planResponse.data directly instead of getDropdownOptions() to avoid closure issues
            if (inviteRows.length > 0 && planResponse.data.pricing_options) {
              setInviteRows(prev => prev.map(row => {
                if (!row.plan_pricing_id) {
                  // Find matching pricing option based on action
                  const action = row.action || 'send_invite';
                  let matchingPricingOption = null;

                  if (action === 'gift_month') {
                    matchingPricingOption = planResponse.data.pricing_options.find(opt => opt.interval_name === 'monthly');
                  } else if (action === 'gift_year') {
                    matchingPricingOption = planResponse.data.pricing_options.find(opt => opt.interval_name === 'yearly');
                  }

                  if (matchingPricingOption?.plan_pricing_id) {
                    console.log(`Updating row ${row.id} (${row.email}) with plan_pricing_id:`, matchingPricingOption.plan_pricing_id);
                    return { ...row, plan_pricing_id: matchingPricingOption.plan_pricing_id };
                  }
                }
                return row;
              }));
            }
          }
        } catch (error) {
          console.error('Error fetching avatar plan after avatar creation:', error);
        }

        // Show success message with avatar details
        const successMessage = response.msg || `Avatar "${newAvatar.first_name} ${newAvatar.last_name}" created successfully!`;
        toast.success(successMessage, {
          toastId: 'avatar-creation-success'
        });

        // Show uploaded files info if available
        if (newAvatar.uploaded_files && newAvatar.uploaded_files.length > 0) {
          const filesInfo = newAvatar.uploaded_files.map(f => f.filename).join(', ');
          console.log('Uploaded files:', filesInfo);
        }

        // Check if response contains invite_prompt or extracted_emails
        // Flow: create avatar -> response includes invite_prompt/extracted_emails -> show popup for invitation
        const invitePrompt = newAvatar.invite_prompt || response.data?.invite_prompt;
        const extractedEmailsFromResponse = newAvatar.extracted_emails || response.data?.extracted_emails || newAvatar.prediction_summary?.extracted_emails;

        // Prefer invite_prompt.candidates (more structured) over extracted_emails
        let formattedInviteEmails = [];

        if (invitePrompt && invitePrompt.candidates && Array.isArray(invitePrompt.candidates)) {
          // Use invite_prompt.candidates (has email, label, checked)
          formattedInviteEmails = invitePrompt.candidates.map((candidate, index) => {
            const emailAddress = (candidate.email || '').trim();
            if (!emailAddress) return null;
            return {
              id: Date.now() + index + Math.random(),
              email: emailAddress,
              name: candidate.label || candidate.name || emailAddress.split('@')[0] || 'Unknown',
              selected: candidate.checked !== false, // Default to true if not specified
              source: 'create'
            };
          }).filter(email => email !== null);
        } else if (extractedEmailsFromResponse && Array.isArray(extractedEmailsFromResponse) && extractedEmailsFromResponse.length > 0) {
          // Fallback to extracted_emails (array of strings or objects)
          formattedInviteEmails = extractedEmailsFromResponse.map((emailItem, index) => {
            if (typeof emailItem === 'string') {
              const emailAddress = emailItem.trim();
              if (!emailAddress) return null;
              const nameFromEmail = emailAddress.split('@')[0];
              return {
                id: Date.now() + index + Math.random(),
                email: emailAddress,
                name: nameFromEmail.charAt(0).toUpperCase() + nameFromEmail.slice(1),
                selected: true,
                source: 'create'
              };
            }
            const emailAddress = (emailItem.email || emailItem.email_address || '').trim();
            if (!emailAddress) return null;
            return {
              id: Date.now() + index + Math.random(),
              email: emailAddress,
              name: emailItem.name || emailItem.full_name || emailItem.label || emailAddress.split('@')[0] || 'Unknown',
              selected: emailItem.checked !== false,
              source: 'create'
            };
          }).filter(email => email !== null);
        } else if (extractedEmailsFromApi && extractedEmailsFromApi.length > 0) {
          // Use emails from extract-emails API (if already available)
          formattedInviteEmails = extractedEmailsFromApi.map(e => ({ ...e, selected: e.selected !== false, source: 'extract' }));
        } else if (extractedEmailsPromise) {
          // Fallback: wait for extract-emails (started in parallel) if create-avatar didn't return invite emails
          const extracted = await extractedEmailsPromise;
          if (extracted && extracted.length > 0) {
            formattedInviteEmails = extracted.map(e => ({ ...e, selected: e.selected !== false, source: 'extract' }));
          }
        }

        formattedInviteEmails = formattedInviteEmails.filter(e => !isPlaceholderInviteEntry(e.email, e.name));
        if (formattedInviteEmails.length > 0) {
          // Store avatar ID and show invite modal with extracted emails
          setCreatedAvatarId(newAvatar.id);
          setInvitePromptEmails(formattedInviteEmails);
          // Initialize inviteRows with default action (Send Invite) for each row
          const initialInviteRows = formattedInviteEmails.map(e => ({
            ...e,
            selected: e.selected !== false,
            action: 'send_invite', // Default to Send Invite
            plan_id: 1, // plan_id 1 = Send Invitation
            plan_pricing_id: null // Send Invite doesn't need plan_pricing_id
          }));
          setInviteRows(initialInviteRows);
          if (process.env.NODE_ENV === 'development') {
            console.log('Initialized inviteRows:', initialInviteRows);
          }
          setShowInviteModal(true);
          setShowAllInviteRows(false); // Reset to collapsed view
          // Don't redirect yet - wait for user to select emails and send invites
          return;
        }
        // No emails extracted from documents, but user uploaded files — still show invite screen so they can add people manually
        if (actualFiles.length > 0) {
          setCreatedAvatarId(newAvatar.id);
          setInvitePromptEmails([]);
          setInviteRows([]);
          setShowInviteModal(true);
          setShowAllInviteRows(false);
          if (process.env.NODE_ENV === 'development') {
            console.log('No emails extracted from uploads; showing invite modal for manual add.');
          }
          return;
        }

        // Show success message if emails were sent during creation
        if (selectedEmailAddresses.length > 0) {
          toast.success(`Emails sent successfully to ${selectedEmailAddresses.length} recipient(s)!`, {
            toastId: 'emails-sent-success'
          });
        }

        // Clear avatars cache in sessionStorage so chat page fetches fresh avatar list
        sessionStorage.removeItem('avatars_fetched');
        sessionStorage.removeItem('avatars_data');
        sessionStorage.removeItem('has_avatars');

        // Call invitee members API after avatar creation
        const currentUserId = userId || await getUserId();
        if (currentUserId) {
          try {
            await projectsAPI.getInviteeMembers(currentUserId);
            console.log('Invitee members fetched after avatar creation');
          } catch (inviteeError) {
            console.warn('Failed to fetch invitee members after avatar creation:', inviteeError);
            // Don't block navigation if this fails
          }
        }

        // Add a delay before redirecting to allow server to process avatar creation
        // This can help prevent errors when loading avatar details immediately after creation
        setTimeout(() => {
          // Redirect to chat page (NOT avatar details page)
          // This prevents unnecessary API call to /api/avatars/{id}
          console.log('CreateAvatarPage: Navigating to /chat (not /avatar/{id})');
          navigate('/chat', { replace: true }); // Use replace to prevent back button going to create page
        }, 2000); // 2 second delay to allow server processing
      } else {
        // If response doesn't match expected structure, show error
        console.error('Unexpected response structure:', response);
        toast.error('Avatar creation response was not in the expected format. Please try again.', {
          toastId: 'avatar-creation-unexpected-response'
        });
      }
    } catch (error) {
      console.error('Avatar creation error:', error);
      console.error('Error details:', {
        status: error.status || error.statusCode,
        message: error.message,
        response: error.response,
        errorName: error.name
      });

      // Show more specific error messages based on error type
      let errorMessage = error.message || 'Failed to create avatar. Please try again.';

      // Check for nested error structure in response (API returns { error: { msg: "...", error: "..." } })
      if (error.response && error.response.error) {
        const nestedError = error.response.error;
        errorMessage = nestedError.msg || nestedError.error || nestedError.message || errorMessage;
      }

      // For network errors (no status code), preserve the detailed error message from API service
      if (!error.status && !error.statusCode) {
        // Network error - use the detailed message from api.js
        errorMessage = error.message || 'Network error: Unable to connect to the API server. Please check your connection and try again.';
      } else if (error.status >= 500 || error.statusCode >= 500) {
        // Server error - use the message from API response if available, otherwise generic message
        errorMessage = error.message || `Server error (${error.status || error.statusCode}) occurred while creating avatar. Please try again in a few moments. If the problem persists, contact support.`;
      } else if (error.status === 400 || error.statusCode === 400) {
        // Bad request - use the message from API response (may include nested error)
        errorMessage = error.message || 'Invalid data provided. Please check your input and try again.';
      } else if (error.status === 401 || error.statusCode === 401) {
        // Unauthorized
        errorMessage = error.message || 'Your session has expired. Please sign in again.';
        setTimeout(() => {
          navigate('/signin');
        }, 2000);
      } else if (error.status === 422 || error.statusCode === 422) {
        // Validation error - use the detailed message from API response
        errorMessage = error.message || 'Validation error. Please check your input and try again.';
      } else if (error.status === 404 || error.statusCode === 404) {
        // Not found
        errorMessage = error.message || 'Avatar endpoint not found. Please check the API configuration.';
      }

      // Determine auto-close duration based on error type
      const autoCloseDuration = (!error.status && !error.statusCode) || (error.status >= 500 || error.statusCode >= 500)
        ? 8000  // Longer for network/server errors
        : 5000; // Shorter for client errors

      toast.error(errorMessage, {
        toastId: (error.status === 401 || error.statusCode === 401) ? 'session-expired' : 'avatar-creation-error',
        autoClose: autoCloseDuration
      });
    } finally {
      setLoading(false);
    }
  };


  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="create-avatar-page">
      <Navbar />

      {loading && <LoadingSpinner fullScreen text="Creating your avatar..." />}

      <Container className="upload-container">
        <div className="upload-header">
          <h1>Create an Avatar</h1>
        </div>

        <div className="upload-form-wrapper">
          <Form onSubmit={handleSubmit(onSubmit)} className="upload-form">
            {paymentActivatedPlan && (
              <div className="form-section subscription-activated-card" style={{
                backgroundColor: '#f6fff8',
                border: '1px solid #bbf7d0',
                borderRadius: '12px',
                padding: '24px',
                marginBottom: '32px',
                textAlign: 'center'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: '#16a34a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                  }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '22px', color: '#166534', fontWeight: '700' }}>Subscription Activated</h3>
                    <p style={{ margin: 0, fontSize: '15px', color: '#15803d', marginTop: '6px' }}>Your payment has been successfully verified.</p>
                  </div>
                </div>

                <div className="subscription-details-grid" style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '32px',
                  justifyContent: 'center'
                }}>
                  <div className="detail-item" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px', fontWeight: '500' }}>Plan Interval</div>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#334155', textTransform: 'capitalize' }}>{paymentActivatedPlan.plan_interval}</div>
                  </div>

                  <div className="detail-item" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px', fontWeight: '500' }}>Auto Renew</div>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: paymentActivatedPlan.auto_renew ? '#22c55e' : '#94a3b8',
                        marginRight: '8px',
                        display: 'inline-block'
                      }}></span>
                      {paymentActivatedPlan.auto_renew ? 'Enabled' : 'Disabled'}
                    </div>
                  </div>

                  {(() => {
                    // Start of IIFE to calculate price
                    if (!paymentActivatedPlan || !avatarPlans.length) return null;
                    const planId = Number(paymentActivatedPlan.avatar_plan_id);
                    let plan = null;
                    for (const type of avatarPlans) {
                      if (type.plans) {
                        const found = type.plans.find(p => Number(p.id) === planId);
                        if (found) {
                          plan = found;
                          break;
                        }
                      }
                    }
                    if (!plan) return null;
                    const interval = (paymentActivatedPlan.plan_interval || '').toLowerCase();
                    const price = interval === 'yearly' ? plan.yearly_price_usd : plan.monthly_price_usd;

                    if (price) {
                      return (
                        <div className="detail-item" style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px', fontWeight: '500' }}>Payment Amount</div>
                          <div style={{ fontSize: '15px', fontWeight: '600', color: '#334155' }}>${price} USD</div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            )}
            {/* Name Fields */}
            <div className="form-section">
              <label className="form-label-custom">Who is this avatar of? <span className="required-asterisk">*</span></label>
              <Row>
                <Col md={6} className="mb-3 mb-md-0">
                  <Form.Control
                    type="text"
                    placeholder="First name"
                    className={`form-input ${errors.firstName ? 'is-invalid' : ''}`}
                    {...register('firstName', {
                      required: 'First name is required'
                    })}
                  />
                  {errors.firstName && (
                    <div className="error-message">{errors.firstName.message}</div>
                  )}
                </Col>
                <Col md={6}>
                  <Form.Control
                    type="text"
                    placeholder="Last name"
                    className={`form-input ${errors.lastName ? 'is-invalid' : ''}`}
                    {...register('lastName', {
                      required: 'Last name is required'
                    })}
                  />
                  {errors.lastName && (
                    <div className="error-message">{errors.lastName.message}</div>
                  )}
                </Col>
              </Row>
            </div>

            {/* Avatar Type - Hide if payment is activated */}
            {!paymentActivatedPlan && (
              <div className="form-section">
                <label className="form-label-custom">Is this avatar for yourself or someone else? <span className="required-asterisk">*</span></label>
                {loadingPlans ? (
                  <div className="loading-plans">
                    <div className="email-spinner"></div>
                    <span>Loading avatar plans...</span>
                  </div>
                ) : avatarPlans.length > 0 ? (
                  <Row>
                    {avatarPlans.map((avatarTypeItem, index) => {
                      const avatarTypeName = avatarTypeItem.name || '';
                      const isActive = normalizeAvatarTypeName(avatarType).toLowerCase() === normalizeAvatarTypeName(avatarTypeName).toLowerCase();

                      // Get subtitle from plans
                      let subtitle = '';
                      if (avatarTypeItem.plans && avatarTypeItem.plans.length > 0) {
                        const monthlyPlan = avatarTypeItem.plans.find(p => Number(p.monthly_price_usd) > 0);
                        const yearlyPlan = avatarTypeItem.plans.find(p => Number(p.yearly_price_usd) > 0);
                        const dayPlan = avatarTypeItem.plans.find(p => Number(p.day_price_usd) > 0);

                        const freeTrialDays = avatarTypeItem.plans.find(p => p.free_trial_days)?.free_trial_days || 0;
                        const freePlanDays = avatarTypeItem.plans.find(p => (!p.monthly_price_usd && !p.yearly_price_usd && !p.day_price_usd) && p.plan_days)?.plan_days || 0;

                        const isForMyself = normalizeAvatarTypeName(avatarTypeItem.name) === 'for myself' || Number(avatarTypeItem.id) === 1;

                        if (!monthlyPlan && !yearlyPlan && !dayPlan) {
                          if (freeTrialDays > 0) {
                            subtitle = `Free for ${freeTrialDays} days`;
                          } else if (freePlanDays > 0) {
                            subtitle = `Free for ${freePlanDays} days`;
                          } else {
                            subtitle = 'Free';
                          }
                        } else {
                          const prices = [];
                          if (yearlyPlan) prices.push(`$${yearlyPlan.yearly_price_usd}/year`);
                          if (monthlyPlan) prices.push(`$${monthlyPlan.monthly_price_usd}/month`);
                          if (dayPlan) {
                            const days = dayPlan.plan_days || 1;
                            prices.push(`$${dayPlan.day_price_usd} for ${days} day${days > 1 ? 's' : ''}`);
                          }

                          const pricePart = !isForMyself && prices.length > 0 ? prices.join(' or ') : '';

                          let freeText = '';
                          if (freeTrialDays > 0) {
                            freeText = `Free for ${freeTrialDays} days`;
                          } else if (freePlanDays > 0) {
                            freeText = `Free for ${freePlanDays} days`;
                          }

                          if (freeText) {
                            subtitle = pricePart ? `${freeText} • ${pricePart}` : freeText;
                          } else {
                            subtitle = pricePart || 'Free';
                          }
                        }
                      }

                      return (
                        <Col xs={6} key={avatarTypeItem.id || index}>
                          <button
                            type="button"
                            className={`toggle-btn toggle-btn-with-subtitle ${isActive ? 'active' : ''}`}
                            onClick={() => {
                              // Normalize to lowercase for consistency
                              setAvatarType(avatarTypeName.toLowerCase());

                              const isForMyself = normalizeAvatarTypeName(avatarTypeName) === 'for myself' || Number(avatarTypeItem.id) === 1;

                              // Check if it's a paid plan type (loved one)
                              // Only show modal if switching to a paid type that requires selection
                              // AND it is not "for myself" (which should default to free/trial)
                              const hasPaidPlans = !isForMyself && avatarTypeItem.plans && avatarTypeItem.plans.some(
                                p => Number(p.monthly_price_usd) > 0 || Number(p.yearly_price_usd) > 0 || Number(p.day_price_usd) > 0
                              );

                              if (hasPaidPlans) {
                                setShowPlanModal(true);
                              } else {
                                // For free plans, auto-select first plan
                                if (avatarTypeItem.plans && avatarTypeItem.plans.length > 0) {
                                  setSelectedPlanId(avatarTypeItem.plans[0].id);
                                }
                              }
                            }}
                          >
                            <span className="toggle-btn-title">{avatarTypeName}</span>
                            {subtitle && (
                              <span className="toggle-btn-subtitle">{subtitle}</span>
                            )}
                          </button>
                        </Col>
                      );
                    })}
                  </Row>
                ) : (
                  <div className="no-plans-message">No avatar plans available.</div>
                )}
              </div>
            )}

            {/* Plan Selection Indicator */}
            {suggestionFlag && selectedPlanId && (
              <div className="form-section">
                <div className="plan-selected-indicator">
                  <div className="plan-selected-icon">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="plan-selected-text">
                    <div className="plan-selected-title">Selected for plan</div>
                    <div className="plan-selected-subtitle">A plan has been automatically selected for you</div>
                  </div>
                </div>
              </div>
            )}

            {selectedPlan && (
              <div className="form-section">
                <div className="plan-selected-indicator">
                  <div className="plan-selected-text">
                    <div className="plan-selected-title">
                      Selected Plan ({selectedPlan.interval})
                    </div>

                  </div>

                </div>
              </div>
            )}

            {/* Relation Dropdown */}
            <div className="form-section">
              <label className="form-label-custom">Relation <span className="required-asterisk">*</span></label>
              {loadingRelations ? (
                <div className="loading-plans">
                  <div className="email-spinner"></div>
                  <span>Loading relations...</span>
                </div>
              ) : (
                <Dropdown className="w-100" onSelect={(k) => setSelectedRelation(k)}>
                  <Dropdown.Toggle
                    variant="light"
                    className="form-input d-flex align-items-center justify-content-between text-start clean-dropdown-toggle"
                    id="relation-dropdown"
                  >
                    <span>
                      {selectedRelation
                        ? (relations.find(r => r.value === selectedRelation)?.label || selectedRelation)
                        : 'Select a relation'}
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100 shadow-sm border-0" style={{ maxHeight: '300px', overflowY: 'auto', borderRadius: '16px', marginTop: '8px' }}>
                    {relations.map((relation, index) => (
                      <Dropdown.Item
                        key={relation.value || index}
                        eventKey={relation.value}
                        active={selectedRelation === relation.value}
                        className="py-2 px-3"
                      >
                        {relation.label || relation.name || relation.value || `Relation ${index + 1}`}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              )}
              {errors.relation && (
                <div className="error-message">{errors.relation.message}</div>
              )}
            </div>

            {/* File Upload */}
            <div className="form-section">
              <label className="form-label-custom">Upload their memories <span className="required-asterisk">*</span></label>
              <div
                className={`dropzone ${isDragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {/* File input for individual files */}
                <input
                  type="file"
                  id="file-input"
                  multiple
                  onChange={handleFileInput}
                  style={{ display: 'none' }}
                  accept=".txt,.doc,.docx,.pdf,.rtf,.odt,.html,.htm,.eml,.msg,.csv,.json,.md,.log,.zip,.rar,.7z,application/zip,application/x-zip-compressed,application/x-rar-compressed,application/x-7z-compressed"
                />
                {/* Folder input for folder selection */}
                <input
                  type="file"
                  id="folder-input"
                  webkitdirectory=""
                  directory=""
                  multiple
                  onChange={handleFileInput}
                  style={{ display: 'none' }}
                />
                <div
                  className="dropzone-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    document.getElementById('file-input').click();
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <div className="dropzone-text">Drag & Drop written content</div>
                <div className="dropzone-subtext">Emails, messages, journals, blog posts, chat logs, letters, zip/rar archives, or any text file</div>
                <div className="dropzone-actions">
                  <button
                    type="button"
                    className="dropzone-btn dropzone-btn-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      document.getElementById('file-input').click();
                    }}
                  >
                    Select Files
                  </button>
                  <button
                    type="button"
                    className="dropzone-btn dropzone-btn-secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      document.getElementById('folder-input').click();
                    }}
                  >
                    Select Folder
                  </button>
                </div>
                <div className="file-types">
                  <span className="file-type-tag">Emails</span>
                  <span className="file-type-tag">Messages</span>
                  <span className="file-type-tag">Journals</span>
                  <span className="file-type-tag">Documents</span>
                </div>
              </div>

              {/* While extracting: show only "Extracting emails from uploaded files..." — hide file list and other details */}
              {uploadedFiles.length > 0 && extractingEmails && (
                <div className="extraction-only-message">
                  <div className="email-spinner"></div>
                  <span>Extracting emails from uploaded files...</span>
                </div>
              )}

              {/* Upload summary only: number of files + total size (no per-file list) */}
              {uploadedFiles.length > 0 && !extractingEmails && (
                <div className="uploaded-files" ref={uploadedFilesRef}>
                  <div className="total-size-indicator" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px 24px' }}>
                    <span className="total-size-label">
                      {uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''} uploaded
                    </span>
                    <span className={`total-size-value ${(() => {
                      const totalSize = actualFiles.reduce((total, file) => total + file.size, 0);
                      const totalGB = totalSize / (1024 * 1024 * 1024);
                      return totalGB > 5 ? 'size-exceeded' : totalGB > 4 ? 'size-warning' : '';
                    })()}`}>
                      {(() => {
                        const totalSize = actualFiles.reduce((total, file) => total + file.size, 0);
                        return formatFileSize(totalSize);
                      })()} / 5 GB
                    </span>
                  </div>
                  {hasRestoredFilePlaceholdersOnly() && (
                    <div style={{ marginTop: '8px', color: '#b45309', fontSize: '0.9rem' }}>
                      Files shown above are restored references. Please select files again before creating avatar.
                    </div>
                  )}
                </div>
              )}

              {/* Emails found in your files — shown only when not extracting and we have emails */}
              {uploadedFiles.length > 0 && !extractingEmails && extractedEmails.length > 0 && (
                <div className="extracted-emails-section">
                  <div className="extracted-emails-header">
                    <div className="extracted-emails-title">
                      <svg className="email-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span>Emails found in your files</span>
                      <span className="email-count">({extractedEmails.length})</span>
                    </div>
                    <div className="email-actions">
                      <button type="button" className="email-action-btn" onClick={selectAllEmails}>Select all</button>
                      <button type="button" className="email-action-btn" onClick={deselectAllEmails}>Deselect all</button>
                    </div>
                  </div>
                  <div className="extracted-emails-list">
                    {extractedEmails.map((email) => (
                      <label key={email.id} className={`extracted-email-item ${selectedEmails.includes(email.id) ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={selectedEmails.includes(email.id)}
                          onChange={() => toggleEmailSelection(email.id)}
                        />
                        <span className="extracted-email-name">{email.name || (email.email && email.email.split('@')[0]) || '—'}</span>
                        <span className="extracted-email-address">{email.email}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

            </div>

            <CustomButton
              type="submit"
              variant="primary"
              fullWidth
              loading={loading || extractingEmails}
              className="form-submit"
              disabled={loading || extractingEmails}
            >
              {extractingEmails ? 'Extracting emails...' : loading ? 'Creating Avatar...' : 'Create Avatar'}
            </CustomButton>
          </Form>
        </div>
      </Container>

      {/* Plan Selection Modal */}
      <CustomModal
        show={showPlanModal}
        onHide={() => setShowPlanModal(false)}
        title="Choose Your Plan"
        subtitle="Select a plan to create an avatar for a loved one"
      >
        {(() => {
          // Find the selected avatar type (match normalized names: "For Loved Once" <-> "for loved once" / "for a loved one")
          const selectedAvatarType = avatarPlans.find(avatarTypeItem => normalizeAvatarTypeName(avatarTypeItem.name) === normalizeAvatarTypeName(avatarType));
          const availablePlans = selectedAvatarType?.plans || [];

          // Filter plans by type
          const freePlan = availablePlans.find(p =>
            (!p.monthly_price_usd || Number(p.monthly_price_usd) === 0) &&
            (!p.yearly_price_usd || Number(p.yearly_price_usd) === 0) &&
            (!p.day_price_usd || Number(p.day_price_usd) === 0)
          );
          const monthlyPlan = availablePlans.find(p => Number(p.monthly_price_usd) > 0);
          const yearlyPlan = availablePlans.find(p => Number(p.yearly_price_usd) > 0);
          const dayPlan = availablePlans.find(p => Number(p.day_price_usd) > 0);

          // Check which options are available
          const hasFreePlan = !!freePlan;
          const hasMonthlyPlan = !!monthlyPlan;
          const hasYearlyPlan = !!yearlyPlan;
          const hasDayPlan = !!dayPlan;
          const hasMultipleOptions = (hasFreePlan ? 1 : 0) + (hasMonthlyPlan ? 1 : 0) + (hasYearlyPlan ? 1 : 0) + (hasDayPlan ? 1 : 0) > 1;

          // Update selected plan when billing period changes
          const handleBillingPeriodChange = (period) => {
            setBillingPeriod(period);
            if (period === 'free' && freePlan) {
              setSelectedPlanId(freePlan.id);
            } else if (period === 'yearly' && yearlyPlan) {
              setSelectedPlanId(yearlyPlan.id);
            } else if (period === 'monthly' && monthlyPlan) {
              setSelectedPlanId(monthlyPlan.id);
            } else if (period === 'days' && dayPlan) {
              setSelectedPlanId(dayPlan.id);
            }
          };

          // Get current selected plan - prioritize selectedPlanId, then billing period
          const getCurrentPlan = () => {
            // First, check if selectedPlanId matches a plan
            if (selectedPlanId) {
              const selectedPlan = availablePlans.find(p => Number(p.id) === Number(selectedPlanId));
              if (selectedPlan) {
                return selectedPlan;
              }
            }

            // Fallback to billing period-based selection
            if (billingPeriod === 'free') return freePlan;
            if (billingPeriod === 'yearly') return yearlyPlan;
            if (billingPeriod === 'monthly') return monthlyPlan;
            if (billingPeriod === 'days') return dayPlan;

            // Final fallback: try to find any available plan
            return freePlan || monthlyPlan || yearlyPlan || dayPlan;
          };

          const currentPlan = getCurrentPlan();

          // Format plan price
          const getPlanPrice = () => {
            if (!currentPlan) return '';
            if (billingPeriod === 'free' || (!currentPlan.monthly_price_usd && !currentPlan.yearly_price_usd && !currentPlan.day_price_usd)) {
              const freeTrialDays = currentPlan.free_trial_days;
              const freePlanDays = currentPlan.plan_days;
              if (freeTrialDays > 0) return `Free for ${freeTrialDays} days`;
              if (freePlanDays > 0) return `Free for ${freePlanDays} days`;
              return 'Free';
            }
            if (billingPeriod === 'yearly' && currentPlan.yearly_price_usd) {
              return `$${currentPlan.yearly_price_usd}/year`;
            }
            if (billingPeriod === 'monthly' && currentPlan.monthly_price_usd) {
              return `$${currentPlan.monthly_price_usd}/month`;
            }
            if (billingPeriod === 'days' && currentPlan.day_price_usd) {
              const days = currentPlan.plan_days || 1;
              return `$${currentPlan.day_price_usd} for ${days} day${days > 1 ? 's' : ''}`;
            }
            return '';
          };

          const planPrice = getPlanPrice();

          return (
            <>
              {hasMultipleOptions && (
                <div className="billing-toggle" style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '24px' }}>
                  {hasFreePlan && (
                    <button
                      className={`billing-option ${billingPeriod === 'free' ? 'active' : ''}`}
                      onClick={() => handleBillingPeriodChange('free')}
                    >
                      Free
                    </button>
                  )}
                  {hasDayPlan && (
                    <button
                      className={`billing-option ${billingPeriod === 'days' ? 'active' : ''}`}
                      onClick={() => handleBillingPeriodChange('days')}
                    >
                      {dayPlan.plan_days} Days
                    </button>
                  )}
                  {hasMonthlyPlan && (
                    <button
                      className={`billing-option ${billingPeriod === 'monthly' ? 'active' : ''}`}
                      onClick={() => handleBillingPeriodChange('monthly')}
                    >
                      Monthly
                    </button>
                  )}
                  {hasYearlyPlan && (
                    <button
                      className={`billing-option ${billingPeriod === 'yearly' ? 'active' : ''}`}
                      onClick={() => handleBillingPeriodChange('yearly')}
                    >
                      Yearly
                      {yearlyPlan && monthlyPlan && (
                        <span className="billing-save">Save {Math.round((1 - (yearlyPlan.yearly_price_usd / (monthlyPlan.monthly_price_usd * 12))) * 100)}%</span>
                      )}
                    </button>
                  )}
                </div>
              )}

              <div className="plan-selection-single">
                {currentPlan ? (
                  <div className="plan-selection-item selected">
                    <div className="plan-selection-header">
                      <div className="plan-selection-name">{selectedAvatarType?.name || 'Avatar Plan'}</div>
                      <div className="plan-selection-price">
                        {planPrice}
                      </div>
                    </div>
                    <div className="plan-selection-details">
                      {(() => {
                        const tokenLimit = currentPlan.token_limit ?? currentPlan.tokens_limit ?? currentPlan.max_tokens ?? '450';
                        const storageLimit = currentPlan.max_storage_gb ?? currentPlan.storage_limit ?? currentPlan.storage_limit_gb ?? currentPlan.storage ?? '5';
                        const familyMembers = currentPlan.max_family_members ?? currentPlan.family_members ?? currentPlan.family_member_limit ?? currentPlan.members_limit ?? '1';
                        // Use plan_days directly from API response
                        const durationDays = currentPlan.plan_days ?? currentPlan.days ?? currentPlan.free_trial_days ?? '30';
                        return `Duration: ${durationDays} days • Tokens: ${tokenLimit} • Storage: ${storageLimit}GB • Family members: ${familyMembers}`;
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="plan-selection-item selected">
                    <div className="plan-selection-header">
                      <div className="plan-selection-name">{selectedAvatarType?.name || 'Avatar Plan'}</div>
                      <div className="plan-selection-price">No plans available</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Auto-renew option removed */}

              <div className="modal-actions">
                <CustomButton variant="secondary" onClick={() => setShowPlanModal(false)}>
                  Cancel
                </CustomButton>
                <CustomButton
                  variant="primary"
                  onClick={async () => {
                    if (currentPlan) {
                      setSelectedPlanId(currentPlan.id);
                      setShowPlanModal(false);

                      // Check if it's a free plan (no monthly/yearly price)
                      const isFreePlan = !currentPlan.monthly_price_usd && !currentPlan.yearly_price_usd && !currentPlan.day_price_usd;

                      if (isFreePlan) {
                        // For free plans, activate directly without payment modal
                        setProcessingPayment(true);
                        try {
                          const captureRes = await paymentsAPI.capturePaypalOrder({
                            order_id: `FREE-${Date.now()}`,
                            avatar_plan_id: currentPlan.id,
                            plan_interval: 'free_trial',
                            auto_renew: Boolean(autoRenew),
                          });
                          const payload = captureRes?.data || captureRes || {};
                          const activated = {
                            avatar_plan_id: currentPlan.id,
                            plan_interval: 'free_trial',
                            auto_renew: Boolean(autoRenew),
                            subscription_id: payload?.subscription_id || null,
                            invoice_id: payload?.invoice_id || null,
                            invoice_download_url: payload?.invoice_download_url || null,
                          };
                          setPaymentActivatedPlan(activated);
                          persistActivatedPlanForCurrentUser(activated);
                          sessionStorage.setItem('paypal_capture_result', JSON.stringify(activated));
                          toast.success('Free plan activated successfully.');
                        } catch (error) {
                          console.error('Free-plan activation failed:', error);
                          toast.error(error?.message || 'Unable to activate free plan. Please try again.');
                        } finally {
                          setProcessingPayment(false);
                        }
                      } else {
                        // For paid plans, show payment modal
                        setShowPaymentModal(true);
                      }
                    } else {
                      toast.error('Please select a valid plan.');
                    }
                  }}
                >
                  Continue with {planPrice || 'selected plan'}
                </CustomButton>
              </div>
            </>
          );
        })()}
      </CustomModal>

      {/* Payment Modal */}
      <CustomModal
        show={showPaymentModal}
        onHide={() => {
          if (!processingPayment) {
            setShowPaymentModal(false);
            setPendingAvatarData(null);
          }
        }}
        title="Complete Payment"
        subtitle="Please complete your payment to create the avatar"
      >
        {(() => {
          const selectedAvatarType = avatarPlans.find(avatarTypeItem => normalizeAvatarTypeName(avatarTypeItem.name) === normalizeAvatarTypeName(avatarType));
          const availablePlans = selectedAvatarType?.plans || [];
          const monthlyPlan = availablePlans.find(p => p.monthly_price_usd);
          const yearlyPlan = availablePlans.find(p => p.yearly_price_usd);
          const currentPlan = billingPeriod === 'yearly' ? yearlyPlan : monthlyPlan;
          const planPrice = currentPlan
            ? (billingPeriod === 'yearly'
              ? `$${currentPlan.yearly_price_usd}/year`
              : `$${currentPlan.monthly_price_usd}/month`)
            : '';

          return (
            <div className="payment-modal-content">
              <div className="payment-summary">
                <div className="payment-item">
                  <span className="payment-label">Plan:</span>
                  <span className="payment-value">
                    {selectedAvatarType?.name
                      ? (selectedAvatarType.name.toLowerCase() === 'for a loved one' || selectedAvatarType.name.toLowerCase() === 'for loved once'
                        ? 'For a Loved One'
                        : selectedAvatarType.name)
                      : 'Loved One Avatar'}
                  </span>
                </div>
                <div className="payment-item">
                  <span className="payment-label">Billing Period:</span>
                  <span className="payment-value">{billingPeriod === 'yearly' ? 'Yearly' : 'Monthly'}</span>
                </div>
                <div className="payment-item payment-total">
                  <span className="payment-label">Total Amount:</span>
                  <span className="payment-value">{planPrice}</span>
                </div>
                {selectedPlan && (
                  <>
                    <div className="payment-item">
                      <span className="payment-label">Duration:</span>
                      <span className="payment-value">{selectedPlan.plan_days ?? selectedPlan.days ?? selectedPlan.free_trial_days ?? '30'} days</span>
                    </div>
                    <div className="payment-item">
                      <span className="payment-label">Token Limit:</span>
                      <span className="payment-value">{selectedPlan.token_limit ?? selectedPlan.tokens_limit ?? selectedPlan.max_tokens ?? '450'}</span>
                    </div>
                    <div className="payment-item">
                      <span className="payment-label">Storage:</span>
                      <span className="payment-value">{selectedPlan.max_storage_gb ?? selectedPlan.storage_limit ?? selectedPlan.storage_limit_gb ?? selectedPlan.storage ?? '5'}GB</span>
                    </div>
                    <div className="payment-item">
                      <span className="payment-label">Family Members:</span>
                      <span className="payment-value">{selectedPlan.max_family_members ?? selectedPlan.family_members ?? selectedPlan.family_member_limit ?? selectedPlan.members_limit ?? '1'}</span>
                    </div>
                  </>
                )}
              </div>

              <div className="payment-methods">
                <div className="payment-method-header">Select Payment Method</div>
                <button
                  type="button"
                  className="payment-method-btn paypal-btn"
                  onClick={processPayment}
                  disabled={processingPayment}
                >
                  {processingPayment ? (
                    <>
                      <div className="payment-spinner"></div>
                      Preparing checkout...
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.174 1.351 1.05 3.3.927 4.715-.122 1.415-.245 2.83-.122 3.705h3.168c.59 0 1.063.473 1.142 1.055l.49 4.57c.08.582-.4 1.055-.99 1.055h-2.94l-1.14 5.365c-.08.582-.55 1.055-1.14 1.055h-2.94l-1.14 5.365c-.08.582-.55 1.055-1.14 1.055z" />
                      </svg>
                      Continue with PayPal Sandbox
                    </>
                  )}
                </button>
              </div>

              <div className="payment-note">
                <p>This is a demo payment. In production, this would redirect to PayPal for secure payment processing.</p>
              </div>
            </div>
          );
        })()}
      </CustomModal>

      {/* Invite Email Modal - Shown after avatar creation if extracted_emails is returned */}
      <CustomModal
        show={showInviteModal}
        onHide={handleSkipInvites}
        className="invite-modal"
        title="Your avatar is ready"
        subtitle={`Invite family to connect with ${pendingAvatarData?.avatarData?.first_name || pendingAvatarData?.first_name || 'your avatar'}`}
        iconVariant="sage"
        icon={
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        }
        size="lg"
        backdrop="static"
      >
        <div className="invite-modal-content">
          <div className="invite-ready-header">
            <div className="invite-ready-subtitle">
              {inviteRows.length > 0
                ? 'We found these people in the uploaded content:'
                : 'Add someone to invite to this avatar:'}
            </div>
          </div>

          <div className="invite-emails-list">
            {/* When no members: show only "Add someone" and actions. No "X person found / X selected". */}
            {inviteRows.length > 0 && (
              <>
                {/* Summary + Select all / Deselect all - visible only when there are invite rows */}
                <div className="invite-summary">
                  <div className="invite-summary-info">
                    <div className="invite-summary-count">
                      {inviteRows.length} {inviteRows.length === 1 ? 'person' : 'people'} found
                    </div>
                    <div className="invite-summary-selected">
                      {inviteRows.filter(r => r.selected).length} selected
                    </div>
                  </div>
                  <div className="invite-summary-actions">
                    <button
                      type="button"
                      className="invite-select-all-btn"
                      onClick={selectAllInviteRows}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="invite-deselect-all-btn"
                      onClick={deselectAllInviteRows}
                    >
                      Deselect all
                    </button>
                    {inviteRows.length > 3 && (
                      <button
                        type="button"
                        className={`invite-expand-btn ${showAllInviteRows ? 'expanded' : ''}`}
                        onClick={() => setShowAllInviteRows(!showAllInviteRows)}
                      >
                        {showAllInviteRows ? 'Show less' : 'View all'}
                        <svg
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          width="16"
                          height="16"
                          className={showAllInviteRows ? 'arrow-up' : 'arrow-down'}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={showAllInviteRows ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                {/* Show preview or full list based on state - only when there are rows */}
                {inviteRows.length > 3 && !showAllInviteRows ? (
                  <>
                    {/* Show first 3 as preview */}
                    <div className="invite-rows-preview">
                      {inviteRows.slice(0, 3).map((row) => {
                        return (
                          <div
                            key={row.id}
                            data-row-id={row.id}
                            className={`invite-row ${row.selected ? 'selected' : ''}`}
                          >
                            <button
                              type="button"
                              className={`invite-check ${row.selected ? 'checked' : ''}`}
                              onClick={() => toggleInviteRow(row.id)}
                              aria-label={row.selected ? 'Unselect' : 'Select'}
                            >
                              {row.selected ? (
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                </svg>
                              ) : null}
                            </button>

                            <div className="invite-row-info">
                              <div className="invite-row-edit">
                                <input
                                  className="invite-row-input invite-row-name-input"
                                  placeholder="Name"
                                  value={row.name || ''}
                                  onChange={(e) => updateInviteRowField(row.id, 'name', e.target.value)}
                                  readOnly={!row.isManual}
                                />
                                <div className="invite-row-email-wrapper">
                                  <input
                                    className="invite-row-input invite-row-email-input"
                                    placeholder="Email"
                                    value={row.email || ''}
                                    onChange={(e) => updateInviteRowField(row.id, 'email', e.target.value)}
                                    readOnly={!row.isManual}
                                  />
                                  {row.selected && (
                                    <div className="invite-row-action-badge">
                                      {(row.action || 'send_invite') === 'send_invite' ? (
                                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18" title="Send Invite">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                      ) : (row.action === 'gift_month' || row.action === 'gift_year') ? (
                                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18" title={row.action === 'gift_month' ? 'Gift Month' : 'Gift Year'}>
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                                        </svg>
                                      ) : null}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="invite-row-actions">
                              <div className="invite-action-select-wrapper">
                                <button
                                  type="button"
                                  className="invite-action-select-btn"
                                  onClick={(e) => {
                                    fetchAvatarPlan();
                                    const newOpenId = openDropdownId === row.id ? null : row.id;
                                    if (newOpenId === row.id) {
                                      // Decide upward + compute fixed anchor position before opening (prevents scroll jumps)
                                      const opensUp = getShouldOpenUpward(e.currentTarget);
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      setDropdownOpensUpward(prev => ({ ...prev, [row.id]: opensUp }));
                                      setDropdownPosition(prev => ({
                                        ...prev,
                                        [row.id]: {
                                          left: rect.left,
                                          top: opensUp ? (rect.top - EST_DROPDOWN_HEIGHT - 4) : (rect.bottom + 4),
                                          width: rect.width
                                        }
                                      }));
                                    }
                                    setOpenDropdownId(newOpenId);
                                    if (newOpenId === row.id) {
                                      checkDropdownPosition(e.currentTarget, row.id);
                                    }
                                  }}
                                >
                                  <div className="invite-action-icon">
                                    {getOptionIcon(row.action || 'send_invite')}
                                  </div>
                                  <span className="invite-action-text">
                                    {getDropdownOptions().find(opt => opt.value === (row.action || 'send_invite'))?.label || 'Send Invite'}
                                  </span>
                                  <div className="invite-action-arrow">
                                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="12" height="12">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={openDropdownId === row.id ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                                    </svg>
                                  </div>
                                </button>
                                {openDropdownId === row.id && (
                                  <div
                                    className="invite-action-dropdown-menu dropdown-fixed"
                                    data-row-id={row.id}
                                    style={{
                                      position: 'fixed',
                                      left: dropdownPosition[row.id]?.left ?? 0,
                                      top: dropdownPosition[row.id]?.top ?? 0,
                                      width: dropdownPosition[row.id]?.width ?? undefined,
                                      zIndex: 2000
                                    }}
                                  >
                                    {getDropdownOptions().map((option) => (
                                      <button
                                        key={option.value}
                                        type="button"
                                        className={`invite-action-dropdown-item ${(row.action || 'send_invite') === option.value ? 'active' : ''}`}
                                        onClick={() => {
                                          const newAction = option.value;
                                          if (process.env.NODE_ENV === 'development') {
                                            console.log(`Changing action for row ${row.id} (${row.email}):`, {
                                              oldAction: row.action,
                                              newAction,
                                              fullRow: row
                                            });
                                          }
                                          setInviteRowAction(row.id, newAction);
                                          setOpenDropdownId(null);
                                        }}
                                      >
                                        <div className="invite-action-dropdown-icon">
                                          {getOptionIcon(option.value)}
                                        </div>
                                        <span>{option.label}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>

                              <button
                                type="button"
                                className="invite-row-remove"
                                onClick={() => removeInviteRow(row.id)}
                                aria-label="Remove"
                                title="Remove"
                              >
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  /* Full list view with scrolling */
                  <div
                    className="invite-rows-full-list"
                    style={{ overflowY: 'auto', overflowX: 'visible' }}
                  >
                    {inviteRows.map((row) => {
                      return (
                        <div
                          key={row.id}
                          data-row-id={row.id}
                          className={`invite-row ${row.selected ? 'selected' : ''}`}
                        >
                          <button
                            type="button"
                            className={`invite-check ${row.selected ? 'checked' : ''}`}
                            onClick={() => toggleInviteRow(row.id)}
                            aria-label={row.selected ? 'Unselect' : 'Select'}
                          >
                            {row.selected ? (
                              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                              </svg>
                            ) : null}
                          </button>

                          <div className="invite-row-info">
                            <div className="invite-row-edit">
                              <input
                                className="invite-row-input invite-row-name-input"
                                placeholder="Name"
                                value={row.name || ''}
                                onChange={(e) => updateInviteRowField(row.id, 'name', e.target.value)}
                                readOnly={!row.isManual}
                              />
                              <div className="invite-row-email-wrapper">
                                <input
                                  className="invite-row-input invite-row-email-input"
                                  placeholder="Email"
                                  value={row.email || ''}
                                  onChange={(e) => updateInviteRowField(row.id, 'email', e.target.value)}
                                  readOnly={!row.isManual}
                                />
                                {row.selected && (
                                  <div className="invite-row-action-badge">
                                    {(row.action || 'send_invite') === 'send_invite' ? (
                                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18" title="Send Invite">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                      </svg>
                                    ) : (row.action === 'gift_month' || row.action === 'gift_year') ? (
                                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18" title={row.action === 'gift_month' ? 'Gift Month' : 'Gift Year'}>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                                      </svg>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="invite-row-actions">
                            <div className="invite-action-select-wrapper">
                              <button
                                type="button"
                                className="invite-action-select-btn"
                                onClick={(e) => {
                                  fetchAvatarPlan();
                                  const newOpenId = openDropdownId === row.id ? null : row.id;
                                  if (newOpenId === row.id) {
                                    // Decide upward + compute fixed anchor position before opening (prevents scroll jumps)
                                    const opensUp = getShouldOpenUpward(e.currentTarget);
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setDropdownOpensUpward(prev => ({ ...prev, [row.id]: opensUp }));
                                    setDropdownPosition(prev => ({
                                      ...prev,
                                      [row.id]: {
                                        left: rect.left,
                                        top: opensUp ? (rect.top - EST_DROPDOWN_HEIGHT - 4) : (rect.bottom + 4),
                                        width: rect.width
                                      }
                                    }));
                                  }
                                  setOpenDropdownId(newOpenId);
                                  if (newOpenId === row.id) {
                                    checkDropdownPosition(e.currentTarget, row.id);
                                  }
                                }}
                              >
                                <div className="invite-action-icon">
                                  {getOptionIcon(row.action || 'send_invite')}
                                </div>
                                <span className="invite-action-text">
                                  {getDropdownOptions().find(opt => opt.value === (row.action || 'send_invite'))?.label || 'Send Invite'}
                                </span>
                                <div className="invite-action-arrow">
                                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="12" height="12">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={openDropdownId === row.id ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                                  </svg>
                                </div>
                              </button>
                              {openDropdownId === row.id && (
                                <div
                                  className="invite-action-dropdown-menu dropdown-fixed"
                                  data-row-id={row.id}
                                  style={{
                                    position: 'fixed',
                                    left: dropdownPosition[row.id]?.left ?? 0,
                                    top: dropdownPosition[row.id]?.top ?? 0,
                                    width: dropdownPosition[row.id]?.width ?? undefined,
                                    zIndex: 2000
                                  }}
                                >
                                  {getDropdownOptions().map((option) => (
                                    <button
                                      key={option.value}
                                      type="button"
                                      className={`invite-action-dropdown-item ${(row.action || 'send_invite') === option.value ? 'active' : ''}`}
                                      onClick={() => {
                                        const newAction = option.value;
                                        if (process.env.NODE_ENV === 'development') {
                                          console.log(`Changing action for row ${row.id} (${row.email}):`, {
                                            oldAction: row.action,
                                            newAction,
                                            fullRow: row
                                          });
                                        }
                                        setInviteRowAction(row.id, newAction);
                                        setOpenDropdownId(null);
                                      }}
                                    >
                                      <div className="invite-action-dropdown-icon">
                                        {getOptionIcon(option.value)}
                                      </div>
                                      <span>{option.label}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>

                            <button
                              type="button"
                              className="invite-row-remove"
                              onClick={() => removeInviteRow(row.id)}
                              aria-label="Remove"
                              title="Remove"
                            >
                              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            <div className="invite-add-someone">
              <button type="button" className="invite-add-trigger" onClick={addBlankInviteRow}>
                <span className="invite-add-plus">+</span> Add someone
              </button>
            </div>
          </div>

          <div className="invite-modal-actions">
            <CustomButton
              type="button"
              variant="secondary"
              onClick={handleSkipInvites}
              disabled={sendingInvites}
            >
              Skip
            </CustomButton>
            <CustomButton
              type="button"
              variant="primary"
              onClick={handleSendInviteEmails}
              loading={sendingInvites}
              disabled={inviteRows.filter(r => r.selected).length === 0 || sendingInvites}
            >
              {sendingInvites ? 'Sending...' : `Send Invitations`}
            </CustomButton>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default CreateAvatarPage;
